import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  TablePagination,
  CircularProgress,
  LinearProgress,
  Button,
  Card,
  Grid,
  Chip,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  Popover,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Mail as MailIcon,
  Drafts as DraftsIcon,
  Delete as DeleteIcon,
  Inbox as InboxIcon,
  Send as SendIcon,
  Reply as ReplyIcon,
  ReplyAll as ReplyAllIcon,
  ForwardToInbox as ForwardIcon,
  ReportGmailerrorred as SpamIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  AttachFile as AttachmentIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Sync as SyncIcon,
  Description as DescriptionIcon,
  Visibility as VisibilityIcon,
  Code as CodeIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  Close as CloseIcon,
  PersonAddAlt1 as ClaimIcon,
  AssignmentInd as AssignIcon,
  NoteAdd as NoteIcon,
  History as HistoryIcon,
  OpenInNew as OpenInNewIcon,
  CalendarMonth as CalendarIcon,
  Block as NonGestireIcon
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

const formatImapSyncError = (raw) => {
  const msg = String(raw || '').trim();
  if (!msg) return '';
  const m = msg.toLowerCase();

  if (m.includes('too many simultaneous connections')) {
    return 'Troppe connessioni contemporanee al server di posta. Riprova tra qualche secondo (chiudendo eventuali sessioni aperte) e poi rilancia la sincronizzazione.';
  }
  if (m.includes('invalid credentials') || m.includes('authentication failed') || m.includes('authentification failed') || m.includes('login failed') || m.includes('auth failed')) {
    return 'Accesso IMAP non riuscito: credenziali non valide o autorizzazione mancante. Verifica username/password e le impostazioni del provider.';
  }
  if (m.includes('etimedout') || m.includes('timed out') || m.includes('timeout') || m.includes('imap_timeout')) {
    return 'Timeout di connessione al server di posta. Riprova tra qualche secondo oppure verifica rete/host/porta.';
  }
  if (m.includes('econnrefused') || m.includes('connection refused')) {
    return 'Connessione rifiutata dal server di posta. Verifica host/porta e che il server sia raggiungibile.';
  }
  if (m.includes('enotfound') || m.includes('getaddrinfo')) {
    return 'Server di posta non raggiungibile (host non trovato). Verifica il nome host configurato e la connessione di rete.';
  }

  return msg;
};

const normalizeSyncStatus = (value) => {
  const raw = String(value || '').toLowerCase();
  if (!raw) return null;
  if (raw === 'syncing') return 'running';
  if (raw === 'success') return 'idle';
  if (raw === 'pending') return 'idle';
  return raw;
};

export default function EmailClient() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [account, setAccount] = useState(null);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAccount, setLoadingAccount] = useState(true);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  
  // Search & Filters
  const [search, setSearch] = useState('');
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc');
  const [filterType, setFilterType] = useState('all');
  const [workflowFilter, setWorkflowFilter] = useState('all');
  const [dayFilter, setDayFilter] = useState('');
  const [dayFilterAnchorEl, setDayFilterAnchorEl] = useState(null);
  const [dayFilterDraft, setDayFilterDraft] = useState('');
  const [error, setError] = useState(null);
  const [totalEmails, setTotalEmails] = useState(0);
  
  // Selected Email for Reading
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loadingBody, setLoadingBody] = useState(false);
  const [emailBody, setEmailBody] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncProgress, setSyncProgress] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const [bodyPreviewByUid, setBodyPreviewByUid] = useState({});
  const bodyPreviewCacheRef = useRef({});
  const bodyPreviewCancelRef = useRef(null);
  const syncStatusRef = useRef(syncStatus);
  const refreshAfterSyncRef = useRef(false);

  // Dialog state for viewing receipts
  const [receiptDialog, setReceiptDialog] = useState({ open: false, receipt: null, body: null, loading: false });
  const [resyncDialogOpen, setResyncDialogOpen] = useState(false);
  const [resyncSubmitting, setResyncSubmitting] = useState(false);
  const [folderSyncDialogOpen, setFolderSyncDialogOpen] = useState(false);
  const [folderSyncSubmitting, setFolderSyncSubmitting] = useState(false);

  // Compose Email State
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeTab, setComposeTab] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [composeOriginalAttachmentsLoading, setComposeOriginalAttachmentsLoading] = useState(false);
  const [composeData, setComposeData] = useState({
      to: '',
      cc: '',
      bcc: '',
      subject: '',
      body: '',
      priority: 'normal',
      deliveryReceipt: false,
      readReceipt: false,
      attachments: [],
      related_uid: null,
      related_folder: null,
      related_action: null
  });

  const [claimDialog, setClaimDialog] = useState({ open: false, email: null });
  const [claimPriority, setClaimPriority] = useState('media');
  const [claimNote, setClaimNote] = useState('');
  const [claimSubmitting, setClaimSubmitting] = useState(false);

  const [noteDialog, setNoteDialog] = useState({ open: false, email: null });
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('operativa');
  const [noteStatusChange, setNoteStatusChange] = useState('nessuna');
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  const [assignDialog, setAssignDialog] = useState({ open: false, email: null });
  const [assignOperatoreId, setAssignOperatoreId] = useState(null);
  const [assignPriority, setAssignPriority] = useState('media');
  const [assignDueDate, setAssignDueDate] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignOperators, setAssignOperators] = useState([]);
  const [assignOperatorsLoading, setAssignOperatorsLoading] = useState(false);
  const [assignOperatorsSearch, setAssignOperatorsSearch] = useState('');

  const [nonGestireDialog, setNonGestireDialog] = useState({ open: false, email: null });
  const [nonGestireMotivo, setNonGestireMotivo] = useState('newsletter');
  const [nonGestireDettagli, setNonGestireDettagli] = useState('');
  const [nonGestireSubmitting, setNonGestireSubmitting] = useState(false);

  const [historyDialog, setHistoryDialog] = useState({ open: false, email: null });
  const [historyTab, setHistoryTab] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyAttachmentsLoading, setHistoryAttachmentsLoading] = useState(false);
  const [historyAttachments, setHistoryAttachments] = useState([]);
  const [historySelectedAttachment, setHistorySelectedAttachment] = useState(null);
  const [historyPreviewLoading, setHistoryPreviewLoading] = useState(false);
  const [historyPreviewUrl, setHistoryPreviewUrl] = useState('');
  const [historyPreviewType, setHistoryPreviewType] = useState('');
  const [historyPreviewError, setHistoryPreviewError] = useState('');

  const openClaimDialog = (email) => {
    setClaimPriority('media');
    setClaimNote('');
    setClaimDialog({ open: true, email });
  };

  const closeClaimDialog = () => {
    if (claimSubmitting) return;
    setClaimDialog({ open: false, email: null });
  };

  const openNoteDialog = (email) => {
    setNoteText('');
    setNoteType('operativa');
    setNoteStatusChange('nessuna');
    setNoteDialog({ open: true, email });
  };

  const closeNoteDialog = () => {
    if (noteSubmitting) return;
    setNoteDialog({ open: false, email: null });
  };

  const openNonGestireDialog = (email) => {
    setNonGestireMotivo('newsletter');
    setNonGestireDettagli('');
    setNonGestireDialog({ open: true, email });
  };

  const closeNonGestireDialog = () => {
    if (nonGestireSubmitting) return;
    setNonGestireDialog({ open: false, email: null });
  };

  const loadAssignOperators = useCallback(async (searchTerm = '') => {
    if (!accountId) return;
    setAssignOperatorsLoading(true);
    try {
      const res = await api.get(`/email-accounts/${accountId}/operatori`, {
        params: { search: searchTerm || '' }
      });
      setAssignOperators(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setAssignOperators([]);
      toast.error('Errore nel recupero operatori');
    } finally {
      setAssignOperatorsLoading(false);
    }
  }, [accountId]);

  const openAssignDialog = (email) => {
    setAssignOperatoreId(null);
    setAssignPriority('media');
    setAssignDueDate('');
    setAssignOperatorsSearch('');
    setAssignOperators([]);
    setAssignDialog({ open: true, email });
    loadAssignOperators('');
  };

  const closeAssignDialog = () => {
    if (assignSubmitting) return;
    setAssignDialog({ open: false, email: null });
  };

  const openHistoryDialog = (email) => {
    setHistoryTab(0);
    setHistoryLoading(true);
    setHistoryData(null);
    setHistoryAttachmentsLoading(true);
    setHistoryAttachments([]);
    setHistorySelectedAttachment(null);
    setHistoryPreviewLoading(false);
    setHistoryPreviewUrl('');
    setHistoryPreviewType('');
    setHistoryPreviewError('');
    setHistoryDialog({ open: true, email });
    api
      .get(`/email-accounts/${accountId}/emails/${email.uid}/history`, { params: { folder: currentFolder } })
      .then((res) => {
        setHistoryData(res.data || null);
      })
      .catch(() => {
        toast.error('Errore nel recupero cronologia');
      })
      .finally(() => {
        setHistoryLoading(false);
      });

    api
      .get(`/email-accounts/${accountId}/emails/${email.uid}/attachments`, { params: { folder: currentFolder } })
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : [];
        setHistoryAttachments(data);
        if (data.length) {
          setHistorySelectedAttachment(data[0]);
          loadHistoryAttachmentPreview(data[0]);
        }
      })
      .catch(() => {
        setHistoryAttachments([]);
      })
      .finally(() => {
        setHistoryAttachmentsLoading(false);
      });
  };

  const closeHistoryDialog = () => {
    if (historyPreviewUrl) {
      try {
        window.URL.revokeObjectURL(historyPreviewUrl);
      } catch {}
    }
    setHistoryDialog({ open: false, email: null });
  };

  useEffect(() => {
    if (!assignDialog.open) return;
    const t = setTimeout(() => {
      loadAssignOperators(assignOperatorsSearch);
    }, 250);
    return () => clearTimeout(t);
  }, [assignDialog.open, assignOperatorsSearch, loadAssignOperators]);

  const handleWorkflowAction = (action, email) => {
    if (action === 'claim') {
      openClaimDialog(email);
      return;
    }
    if (action === 'assign') {
      openAssignDialog(email);
      return;
    }
    if (action === 'note') {
      openNoteDialog(email);
      return;
    }
    if (action === 'non_gestire') {
      openNonGestireDialog(email);
      return;
    }
    if (action === 'history') {
      openHistoryDialog(email);
      return;
    }
    toast.info(`${action} (simulazione)`);
  };

  const formatHistoryWhen = (dateValue) => {
    if (!dateValue) return '';
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    const time = formatTimeHHMM(d);
    if (sameDay) return `oggi ${time}`;
    const day = d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
    return `${day} ${time}`;
  };

  const formatItalianDay = (isoDay) => {
    if (!isoDay) return '';
    const raw = String(isoDay);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const d = new Date(`${raw}T00:00:00`);
    if (Number.isNaN(d.getTime())) return raw;
    return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getTimezoneOffsetForDayStart = (isoDay, addDays = 0) => {
    const raw = String(isoDay || '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
    const d = new Date(`${raw}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    if (addDays) d.setDate(d.getDate() + Number(addDays));
    const off = d.getTimezoneOffset();
    return Number.isFinite(off) ? off : null;
  };

  const buildBaseTimeline = (email) => {
    const baseDate = email?.date ? new Date(email.date) : null;
    const items = [];

    if (email?.is_read) {
      items.push({
        type: 'lettura',
        titolo: 'Messaggio letto',
        tag: 'lettura',
        tagColor: 'default',
        when: baseDate || new Date(),
        descrizione: 'Messaggio aperto.'
      });
    }

    if (baseDate) {
      items.push({
        type: 'ricezione',
        titolo: 'Messaggio ricevuto',
        tag: 'ricezione',
        tagColor: 'success',
        when: baseDate,
        descrizione: `Ricevuto su ${account?.email || ''} da ${cleanSenderName(email?.from || '')} via ${isPecAccount ? 'PEC' : 'E-mail'}.`
      });
    }

    if (!items.length) {
      items.push({
        type: 'ricezione',
        titolo: 'Messaggio',
        tag: 'ricezione',
        tagColor: 'success',
        when: new Date(),
        descrizione: 'Nessuna attività registrata.'
      });
    }

    return items;
  };

  const buildMockTimeline = (email) => {
    const baseDate = email?.date ? new Date(email.date) : new Date();
    const t = (minutesDelta) => new Date(baseDate.getTime() + minutesDelta * 60 * 1000);
    return [
      {
        type: 'risposta',
        titolo: 'Risposta inviata',
        tag: 'risposta',
        tagColor: 'success',
        when: t(130),
        descrizione: `Risposta inviata da ${user?.name || 'Operatore'}. Oggetto: Re: ${cleanSubject(email?.subject || '')}`
      },
      {
        type: 'nota',
        titolo: 'Nota interna',
        tag: 'nota',
        tagColor: 'warning',
        when: t(95),
        descrizione: 'Verificato contratto n. 4521 in archivio. Cliente contesta firma su integrazione del 15/03.',
        highlight: true
      },
      {
        type: 'stato',
        titolo: 'Stato cambiato',
        tag: 'stato',
        tagColor: 'info',
        when: t(70),
        descrizione: `${user?.name || 'Operatore'}: Nuovo → In carico`
      },
      {
        type: 'assegnazione',
        titolo: 'Preso in carico',
        tag: 'assegnazione',
        tagColor: 'secondary',
        when: t(70),
        descrizione: `${user?.name || 'Operatore'} ha preso in carico il messaggio. Priorità impostata: Media.`
      },
      {
        type: 'lettura',
        titolo: 'Messaggio letto',
        tag: 'lettura',
        tagColor: 'default',
        when: t(15),
        descrizione: `Aperto da ${user?.name || 'Operatore'} per la prima volta.`
      },
      {
        type: 'ricezione',
        titolo: 'Messaggio ricevuto',
        tag: 'ricezione',
        tagColor: 'success',
        when: baseDate,
        descrizione: `Ricevuto su ${account?.email || ''} da ${cleanSenderName(email?.from || '')} via ${isPecAccount ? 'PEC' : 'E-mail'}.`
      }
    ];
  };

  const parseJsonMaybe = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  const mapBackendTimeline = (events) => {
    const list = Array.isArray(events) ? events : [];
    return list.map((ev) => {
      const tipo = String(ev?.tipo_evento || '').toLowerCase();
      const when = ev?.creato_il ? new Date(ev.creato_il) : null;
      const operatore = ev?.operatore_nome || ev?.operatore_email || 'Operatore';
      const dettagli = parseJsonMaybe(ev?.dettagli) || {};

      if (tipo === 'risposto') {
        return {
          type: 'risposta',
          titolo: 'Risposta inviata',
          tag: 'risposta',
          tagColor: 'success',
          when,
          descrizione: `Risposta inviata da ${operatore}.`
        };
      }
      if (tipo === 'inoltrato') {
        return {
          type: 'risposta',
          titolo: 'Inoltro inviato',
          tag: 'inoltro',
          tagColor: 'success',
          when,
          descrizione: `Inoltro inviato da ${operatore}.`
        };
      }
      if (tipo === 'composizione_aperta') {
        const a = String(dettagli?.azione || dettagli?.action || '').toLowerCase();
        const label = a === 'reply_all' ? 'Rispondi a tutti' : a === 'forward' ? 'Inoltra' : 'Rispondi';
        return {
          type: 'azione',
          titolo: 'Composizione aperta',
          tag: 'azione',
          tagColor: 'info',
          when,
          descrizione: `${operatore} ha aperto: ${label}.`
        };
      }
      if (tipo === 'non_gestire') {
        const motivo = String(dettagli?.motivo || '').trim();
        const det = String(dettagli?.dettagli || '').trim();
        const suffix = motivo ? ` Motivo: ${motivo}${det ? ` — ${det}` : ''}` : '';
        return {
          type: 'non_gestire',
          titolo: 'Non gestire',
          tag: 'non gestire',
          tagColor: 'default',
          when,
          descrizione: `Classificata come non gestire da ${operatore}.${suffix}`
        };
      }
      if (tipo === 'nota_aggiunta') {
        return {
          type: 'nota',
          titolo: 'Nota interna',
          tag: 'nota',
          tagColor: 'warning',
          when,
          descrizione: `Nota aggiunta da ${operatore}.`
        };
      }
      if (tipo === 'cambio_stato') {
        const da = dettagli?.da || null;
        const a = dettagli?.a || null;
        return {
          type: 'stato',
          titolo: 'Stato cambiato',
          tag: 'stato',
          tagColor: 'info',
          when,
          descrizione: da && a ? `${operatore}: ${da} → ${a}` : `Stato aggiornato da ${operatore}.`
        };
      }
      if (tipo === 'preso_in_carico') {
        const p = dettagli?.priorita || dettagli?.priority || null;
        return {
          type: 'assegnazione',
          titolo: 'Preso in carico',
          tag: 'assegnazione',
          tagColor: 'secondary',
          when,
          descrizione: p ? `${operatore} ha preso in carico il messaggio. Priorità impostata: ${String(p)}.` : `${operatore} ha preso in carico il messaggio.`
        };
      }
      if (tipo === 'assegnato' || tipo === 'riassegnato') {
        const assegnatoA =
          ev?.assegnato_a_nome ||
          ev?.assegnato_a_email ||
          (dettagli?.a_user_id != null ? `Utente #${String(dettagli.a_user_id)}` : null);
        const assegnatoDa =
          ev?.assegnato_da_nome ||
          ev?.assegnato_da_email ||
          (dettagli?.da_user_id != null ? `Utente #${String(dettagli.da_user_id)}` : null);
        const base =
          assegnatoA
            ? `${tipo === 'riassegnato' ? 'Riassegnato' : 'Assegnato'} a ${assegnatoA} da ${operatore}${tipo === 'riassegnato' && assegnatoDa ? ` (prima: ${assegnatoDa})` : ''}.`
            : tipo === 'riassegnato'
              ? `Riassegnato da ${operatore}.`
              : `Assegnato da ${operatore}.`;
        return {
          type: 'assegnazione',
          titolo: tipo === 'riassegnato' ? 'Riassegnato' : 'Assegnato',
          tag: 'assegnazione',
          tagColor: 'secondary',
          when,
          descrizione: base
        };
      }
      if (tipo === 'letto') {
        return {
          type: 'lettura',
          titolo: 'Messaggio letto',
          tag: 'lettura',
          tagColor: 'default',
          when,
          descrizione: `Aperto da ${operatore}.`
        };
      }
      if (tipo === 'importato') {
        return {
          type: 'ricezione',
          titolo: 'Messaggio importato',
          tag: 'ricezione',
          tagColor: 'success',
          when,
          descrizione: 'Importato dal sistema.'
        };
      }

      return {
        type: 'stato',
        titolo: 'Evento',
        tag: tipo || 'evento',
        tagColor: 'default',
        when,
        descrizione: `Evento registrato da ${operatore}.`
      };
    });
  };

  const getTimelineDotColor = (type) => {
    if (type === 'risposta') return 'success.main';
    if (type === 'nota') return 'warning.main';
    if (type === 'stato') return 'info.main';
    if (type === 'azione') return 'info.main';
    if (type === 'assegnazione') return 'secondary.main';
    if (type === 'lettura') return 'text.secondary';
    if (type === 'non_gestire') return 'error.main';
    return 'success.main';
  };

  const getTimelineIcon = (type) => {
    if (type === 'risposta') return <SendIcon sx={{ fontSize: 16 }} />;
    if (type === 'azione') return <EditIcon sx={{ fontSize: 16 }} />;
    if (type === 'nota') return <NoteIcon sx={{ fontSize: 16 }} />;
    if (type === 'stato') return <SyncIcon sx={{ fontSize: 16 }} />;
    if (type === 'assegnazione') return <AssignIcon sx={{ fontSize: 16 }} />;
    if (type === 'lettura') return <VisibilityIcon sx={{ fontSize: 16 }} />;
    if (type === 'non_gestire') return <NonGestireIcon sx={{ fontSize: 16, color: 'error.main' }} />;
    return <InboxIcon sx={{ fontSize: 16 }} />;
  };

  const logComposeAction = async (email, action) => {
    if (!accountId || !email?.uid) return;
    try {
      await api.post(
        `/email-accounts/${accountId}/emails/${email.uid}/compose-action`,
        { action },
        { params: { folder: currentFolder } }
      );
      if (historyDialog.open && historyDialog.email?.uid === email.uid) {
        const res = await api.get(`/email-accounts/${accountId}/emails/${email.uid}/history`, { params: { folder: currentFolder } });
        setHistoryData(res.data || null);
      }
    } catch (e) {}
  };

  const handleComposeOpen = () => {
      setComposeOriginalAttachmentsLoading(false);
      setComposeData({
          to: '',
          cc: '',
          bcc: '',
          subject: '',
          body: '',
          priority: 'normal',
          deliveryReceipt: false,
          readReceipt: false,
          attachments: [],
          related_uid: null,
          related_folder: null,
          related_action: null
      });
      setComposeTab(0);
      setComposeOpen(true);
  };

  const handleReplyOpen = () => {
    if (!selectedEmail) return;
    logComposeAction(selectedEmail, 'reply');
    setComposeOriginalAttachmentsLoading(false);
    const to = (() => {
      if (isSentFolderPath(currentFolder)) {
        const recipients = extractEmailAddresses(selectedEmail.to);
        return recipients.length ? joinEmails(recipients) : cleanRecipientName(selectedEmail.to);
      }
      const sender = extractEmailAddresses(selectedEmail.from);
      return sender.length ? sender[0] : cleanSenderName(selectedEmail.from);
    })();
    const originalText =
      (emailBody?.text || emailBody?.originalText || '').trim() ||
      stripHtmlToText(emailBody?.html || emailBody?.originalHtml);
    const originalTextNormalized = String(originalText || '').replace(/\r\n/g, '\n').trim();
    const quoted = originalTextNormalized
      ? originalTextNormalized
          .split('\n')
          .map((line) => `> ${line}`)
          .join('\n')
      : '';
    const originalDate = selectedEmail.date ? new Date(selectedEmail.date).toLocaleString() : '';
    const headerLines = [
      '---- Messaggio originale ----',
      `Da: ${cleanSenderName(selectedEmail.from)}`,
      originalDate ? `Data: ${originalDate}` : null,
      `Oggetto: ${selectedEmail.subject || ''}`
    ].filter(Boolean);
    const body = `\n\n${headerLines.join('\n')}\n\n${quoted}`.trimStart();

    setComposeData({
      to,
      cc: '',
      bcc: '',
      subject: buildReplySubject(selectedEmail.subject),
      body,
      priority: 'normal',
      deliveryReceipt: false,
      readReceipt: false,
      attachments: [],
      related_uid: selectedEmail.uid,
      related_folder: currentFolder,
      related_action: 'reply'
    });
    setComposeTab(0);
    setComposeOpen(true);
  };

  const handleReplyAllOpen = () => {
    if (!selectedEmail) return;
    logComposeAction(selectedEmail, 'reply_all');
    setComposeOriginalAttachmentsLoading(false);
    const accountEmail = String(account?.email || '').toLowerCase();
    const sentFolder = isSentFolderPath(currentFolder);

    const sender = extractEmailAddresses(selectedEmail.from);
    const toEmails = sentFolder
      ? extractEmailAddresses(selectedEmail.to)
      : sender.length
        ? [sender[0]]
        : [cleanSenderName(selectedEmail.from)].filter(Boolean);

    const allRecipients = [
      ...extractEmailAddresses(selectedEmail.to),
      ...extractEmailAddresses(selectedEmail.cc)
    ];

    const toLower = new Set(toEmails.map((e) => String(e).toLowerCase()));
    const senderLower = new Set(sender.map((e) => String(e).toLowerCase()));

    const ccEmails = [];
    const ccSeen = new Set();
    for (const e of allRecipients) {
      const key = String(e).toLowerCase();
      if (accountEmail && key === accountEmail) continue;
      if (!sentFolder && senderLower.has(key)) continue;
      if (toLower.has(key)) continue;
      if (ccSeen.has(key)) continue;
      ccSeen.add(key);
      ccEmails.push(e);
    }

    const originalText =
      (emailBody?.text || emailBody?.originalText || '').trim() ||
      stripHtmlToText(emailBody?.html || emailBody?.originalHtml);
    const originalTextNormalized = String(originalText || '').replace(/\r\n/g, '\n').trim();
    const quoted = originalTextNormalized
      ? originalTextNormalized
          .split('\n')
          .map((line) => `> ${line}`)
          .join('\n')
      : '';
    const originalDate = selectedEmail.date ? new Date(selectedEmail.date).toLocaleString() : '';
    const headerLines = [
      '---- Messaggio originale ----',
      `Da: ${cleanSenderName(selectedEmail.from)}`,
      originalDate ? `Data: ${originalDate}` : null,
      `Oggetto: ${selectedEmail.subject || ''}`
    ].filter(Boolean);
    const body = `\n\n${headerLines.join('\n')}\n\n${quoted}`.trimStart();

    setComposeData({
      to: joinEmails(toEmails),
      cc: joinEmails(ccEmails),
      bcc: '',
      subject: buildReplySubject(selectedEmail.subject),
      body,
      priority: 'normal',
      deliveryReceipt: false,
      readReceipt: false,
      attachments: [],
      related_uid: selectedEmail.uid,
      related_folder: currentFolder,
      related_action: 'reply_all'
    });
    setComposeTab(0);
    setComposeOpen(true);
  };

  const handleForwardOpen = () => {
    if (!selectedEmail) return;
    logComposeAction(selectedEmail, 'forward');
    setComposeOriginalAttachmentsLoading(false);
    const originalText =
      (emailBody?.text || emailBody?.originalText || '').trim() ||
      stripHtmlToText(emailBody?.html || emailBody?.originalHtml);
    const originalTextNormalized = String(originalText || '').replace(/\r\n/g, '\n').trim();
    const originalDate = selectedEmail.date ? new Date(selectedEmail.date).toLocaleString() : '';
    const headerLines = [
      '---- Messaggio inoltrato ----',
      `Da: ${cleanSenderName(selectedEmail.from)}`,
      originalDate ? `Data: ${originalDate}` : null,
      `Oggetto: ${selectedEmail.subject || ''}`,
      selectedEmail.to ? `A: ${cleanRecipientName(selectedEmail.to)}` : null
    ].filter(Boolean);
    const body = `\n\n${headerLines.join('\n')}\n\n${originalTextNormalized}`.trimStart();

    setComposeData({
      to: '',
      cc: '',
      bcc: '',
      subject: buildForwardSubject(selectedEmail.subject),
      body,
      priority: 'normal',
      deliveryReceipt: false,
      readReceipt: false,
      attachments: [],
      related_uid: selectedEmail.uid,
      related_folder: currentFolder,
      related_action: 'forward'
    });
    setComposeTab(0);
    setComposeOpen(true);
    loadSelectedEmailAttachmentsIntoCompose({ includeTechnical: showOriginal });
  };

  const handleComposeClose = () => {
      if (!sendingEmail) setComposeOpen(false);
  };

  const handleComposeChange = (field, value) => {
      setComposeData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
      const files = Array.from(e.target.files);
      setComposeData(prev => ({ ...prev, attachments: [...prev.attachments, ...files] }));
  };

  const removeAttachment = (index) => {
      setComposeData(prev => ({
          ...prev,
          attachments: prev.attachments.filter((_, i) => i !== index)
      }));
  };

  const handleSendEmail = async () => {
      if (!composeData.to || !composeData.subject || !composeData.body) {
          toast.error('Compila i campi obbligatori (Destinatario, Oggetto, Messaggio)');
          return;
      }

      setSendingEmail(true);
      const formData = new FormData();
      formData.append('to', composeData.to);
      formData.append('cc', composeData.cc);
      formData.append('bcc', composeData.bcc);
      formData.append('subject', composeData.subject);
      formData.append('body', composeData.body);
      formData.append('priority', composeData.priority);

      if (composeData.related_uid) formData.append('related_uid', String(composeData.related_uid));
      if (composeData.related_folder) formData.append('related_folder', String(composeData.related_folder));
      if (composeData.related_action) formData.append('related_action', String(composeData.related_action));

      if (!isPecAccount) {
        formData.append('deliveryReceipt', composeData.deliveryReceipt);
        formData.append('readReceipt', composeData.readReceipt);
      }
      
      composeData.attachments.forEach(file => {
          formData.append('attachments', file);
      });

      try {
          await api.post(`/email-accounts/${accountId}/send`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success(isPecAccount ? 'PEC inviata con successo' : 'Email inviata con successo');
          setComposeOpen(false);
          // Optionally refresh sent folder if we are in it, or just let it be
      } catch (error) {
          console.error('Error sending email:', error);
          const data = error?.response?.data || {};
          const traceId =
            data.traceId ||
            data.trace_id ||
            error?.response?.headers?.['x-trace-id'] ||
            error?.response?.headers?.['X-Trace-Id'];
          const code = data.code ? String(data.code) : null;
          const backendMessage = data.message ? String(data.message) : '';
          const technicalMessage = data.error ? String(data.error) : '';
          const status = error?.response?.status;

          const label = isPecAccount ? 'PEC' : 'email';
          const idPart = traceId ? ` (ID: ${traceId})` : '';

          let uiMessage = '';
          if (code === 'SMTP_AUTH_FAILED') {
            uiMessage = `Autenticazione ${label} non riuscita. Verifica credenziali e impostazioni SMTP${idPart}`;
          } else if (code === 'SMTP_CONNECTION_FAILED') {
            uiMessage = `Server di posta non raggiungibile o in timeout. Riprova${idPart}`;
          } else if (code === 'SMTP_RECIPIENT_REJECTED') {
            uiMessage = `Destinatario non valido o rifiutato dal server di posta${idPart}`;
          } else if (backendMessage) {
            uiMessage = `${backendMessage}${idPart}`;
          } else if (status) {
            uiMessage = `Errore ${status} durante l'invio della ${label}${idPart}`;
          } else {
            uiMessage = `Errore durante l'invio della ${label}${idPart}`;
          }

          const detail = (technicalMessage || '').trim();
          const detailShort =
            detail && detail.length > 180 ? `${detail.slice(0, 177)}...` : detail;
          if (detailShort) {
            uiMessage = `${uiMessage}: ${detailShort}`;
          }

          toast.error(uiMessage);
      } finally {
          setSendingEmail(false);
      }
  };
  
  // Check if current account is PEC
  const isPecAccount = account?.type === 'pec' || account?.email?.toLowerCase().includes('pec') || account?.provider?.toLowerCase().includes('pec');

  // Add function to open receipt details
  const handleOpenReceipt = async (receipt) => {
      setReceiptDialog({ open: true, receipt, body: null, loading: true });
      try {
          const response = await api.get(`/email-accounts/${accountId}/emails/${receipt.uid}/body`, {
              params: { folder: receipt.folder, mark_seen: 0 } // non segnare come letto
          });
          setReceiptDialog(prev => ({ ...prev, body: response.data, loading: false }));
      } catch (error) {
          console.error('Error loading receipt body:', error);
          toast.error('Errore caricamento ricevuta');
          setReceiptDialog(prev => ({ ...prev, loading: false }));
      }
  };

  const handleCloseReceipt = () => {
      setReceiptDialog({ open: false, receipt: null, body: null, loading: false });
  };
  
  const handleDownloadReceiptAttachment = async (receipt, idx, filename) => {
    try {
      const response = await api.get(`/email-accounts/${accountId}/emails/${receipt.uid}/attachments/${idx}/download`, {
        params: { folder: receipt.folder },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'allegato';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore download allegato ricevuta:', error);
      toast.error('Errore nel download dell\'allegato');
    }
  };

  const fetchAccount = useCallback(async () => {
    try {
      const response = await api.get(`/email-accounts/${accountId}`);
      setAccount(response.data);
    } catch (error) {
      console.error('Error fetching account:', error);
      toast.error('Errore nel caricamento dell\'account: ' + (error.response?.data?.message || error.message));
      // navigate('/email-accounts'); // Disable auto-redirect for debugging
    } finally {
      setLoadingAccount(false);
    }
  }, [accountId, navigate]);

  const [currentFolder, setCurrentFolder] = useState('INBOX');
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [folderError, setFolderError] = useState(null);

  // Fetch folders
  const fetchFolders = useCallback(() => {
    if (accountId) {
      setLoadingFolders(true);
      api.get(`/email-accounts/${accountId}/folders`)
        .then(res => {
            console.log('Folders fetched:', res.data);
            if (Array.isArray(res.data)) {
              setFolders(res.data);
              setFolderError(null);
            } else {
              throw new Error('Formato risposta non valido');
            }
        })
        .catch(err => {
            console.error('Error fetching folders:', err);
            const status = err?.response?.status;
            const code = err?.response?.data?.code;
            const backendMsg = err?.response?.data?.message;
            const rawMsg = err?.message || 'Errore sconosciuto';

            if (code === 'IMAP_FOLDERS_FETCH_FAILED' || status === 503) {
              setFolderError(
                backendMsg ||
                  'Impossibile caricare le cartelle dal server di posta. Riprova tra qualche secondo (potrebbe essere un limite di connessioni simultanee o un timeout).'
              );
              return;
            }

            if (status === 403) {
              setFolderError('Accesso negato: non hai i permessi per vedere le cartelle di questa casella.');
              return;
            }

            setFolderError(backendMsg || rawMsg);
        })
        .finally(() => setLoadingFolders(false));
    }
  }, [accountId]);

  const refreshFolderCounts = useCallback(async () => {
    if (!accountId) return;
    try {
      const res = await api.get(`/email-accounts/${accountId}/folders-counts`);
      const rows = Array.isArray(res.data) ? res.data : [];
      const map = {};
      for (const r of rows) {
        const key = r?.path != null ? String(r.path) : null;
        if (!key) continue;
        map[key] = {
          total: Number(r.total || 0),
          unread: Number(r.unread || 0)
        };
      }
      setFolders((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((f) => {
          const path = typeof f === 'string' ? f : f?.path;
          if (!path) return f;
          const c = map[String(path)];
          if (!c) return f;
          if (typeof f === 'string') return f;
          return { ...f, total: c.total, unread: c.unread };
        });
      });
    } catch (e) {}
  }, [accountId]);

  const fetchEmails = useCallback(async (overrides = null) => {
    if (!accountId) return;

    setLoading(true);
    setError(null);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search,
        orderBy: orderBy,
        order: order,
        filter: filterType,
        workflowFilter,
        day: dayFilter || undefined,
        folder: currentFolder
      };

      if (overrides && typeof overrides === 'object') {
        Object.assign(params, overrides);
      }

      const effectiveDay = params.day != null ? String(params.day) : '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(effectiveDay)) {
        if (params.dayOffsetStart == null) {
          const off = getTimezoneOffsetForDayStart(effectiveDay, 0);
          if (off != null) params.dayOffsetStart = off;
        }
        if (params.dayOffsetEnd == null) {
          const off = getTimezoneOffsetForDayStart(effectiveDay, 1);
          if (off != null) params.dayOffsetEnd = off;
        }
      } else {
        delete params.dayOffsetStart;
        delete params.dayOffsetEnd;
      }
      
      const response = await api.get(`/email-accounts/${accountId}/emails`, { params });
      
      // Handle the new response format { emails: [], total: 0, ... }
      if (response.data && Array.isArray(response.data.emails)) {
          setEmails(response.data.emails);
          setTotalEmails(response.data.total || 0);
          prefetchBodyPreviews(response.data.emails, currentFolder);
      } else if (Array.isArray(response.data)) {
          // Fallback for old format
          setEmails(response.data);
          setTotalEmails(response.data.length);
          prefetchBodyPreviews(response.data, currentFolder);
      } else {
          setEmails([]);
          setTotalEmails(0);
      }
      refreshFolderCounts();

      try {
        const statusRes = await api.get(`/email-accounts/${accountId}/sync-status`, {
          params: { folder: currentFolder }
        });
        const status = normalizeSyncStatus(statusRes.data?.sync_status || null);
        if (status !== syncStatusRef.current && status === 'running') {
          toast.info('Sincronizzazione posta avviata…');
        }
        setSyncStatus(status);
        setSyncProgress(statusRes.data?.progress || null);
        setSyncError(statusRes.data?.sync_error ? formatImapSyncError(statusRes.data.sync_error) : null);
      } catch (e) {}

    } catch (err) {
      console.error('Error fetching emails:', err);
      setError('Errore durante il caricamento delle email');
    } finally {
      setLoading(false);
    }
  }, [accountId, page, rowsPerPage, search, orderBy, order, filterType, workflowFilter, dayFilter, currentFolder, refreshFolderCounts]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  useEffect(() => {
    if (accountId) {
      fetchAccount();
      fetchEmails();
    }
  }, [accountId, fetchAccount, fetchEmails]);

  useEffect(() => {
    return () => {
      if (bodyPreviewCancelRef.current) {
        try {
          bodyPreviewCancelRef.current();
        } catch {}
      }
    };
  }, [currentFolder]);

  useEffect(() => {
    if (!accountId) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/email-accounts/${accountId}/sync-status`, {
          params: { folder: currentFolder }
        });
        const status = normalizeSyncStatus(res.data?.sync_status || null);
        const progress = res.data?.progress || null;
        const errorMsg = res.data?.sync_error ? formatImapSyncError(res.data.sync_error) : null;
        if (status !== syncStatus) {
          if (status === 'running') {
            const shouldAutoRefresh = !selectedEmail && page === 0 && !search;
            if (shouldAutoRefresh) {
              refreshAfterSyncRef.current = true;
            }
          }
          if (syncStatus === 'running' && status === 'idle') {
            toast.success('Sincronizzazione completata');
            if (refreshAfterSyncRef.current) {
              refreshAfterSyncRef.current = false;
              setPage(0);
              fetchEmails({ page: 1, no_sync: 1 });
              fetchFolders();
            }
          }
          if (status === 'error') {
            toast.error(errorMsg ? `Errore sincronizzazione: ${errorMsg}` : 'Errore durante la sincronizzazione');
          }
        }
        setSyncStatus(status);
        setSyncProgress(progress);
        setSyncError(errorMsg);
      } catch (e) {}
    }, 2000);
    return () => clearInterval(interval);
  }, [accountId, currentFolder, syncStatus, selectedEmail, page, search, fetchEmails, fetchFolders]);

  const cleanSenderName = (from) => {
    if (!from) return '';
    
    // Check if it matches "Per conto di: Real Sender" <provider@pec.it> format
    // We want "Real Sender" (which is often the actual PEC address), not the provider address
    const perContoMatch = from.match(/^"Per conto di:\s*(.*?)"\s*<.+?>\s*$/i);
    if (perContoMatch) {
        return perContoMatch[1];
    }

    // Standard "Name <email>" format
    const nameEmailMatch = from.match(/^"?(.*?)"?\s*<(.+?)>$/);
    if (nameEmailMatch) {
        // Prefer email address as seen in the screenshot
        return nameEmailMatch[2]; 
    }
    
    // Just <email>
    const emailOnlyMatch = from.match(/^<(.+?)>$/);
    if (emailOnlyMatch) {
        return emailOnlyMatch[1];
    }
    
    return from.replace(/^"|"$/g, '');
  };

  const cleanRecipientName = (to) => {
    if (!to) return '';
    const nameEmailMatch = String(to).match(/^"?(.*?)"?\s*<(.+?)>$/);
    if (nameEmailMatch) {
      return nameEmailMatch[2];
    }
    const emailOnlyMatch = String(to).match(/^<(.+?)>$/);
    if (emailOnlyMatch) {
      return emailOnlyMatch[1];
    }
    return String(to).replace(/^"|"$/g, '');
  };

  const stripHtmlToText = (html) => {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(String(html), 'text/html');
      return String(doc.body?.textContent || '').replace(/\u00a0/g, ' ').trim();
    } catch (e) {
      return String(html)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
  };

  const extractEmailAddresses = (value) => {
    if (!value) return [];
    const matches = String(value).match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi) || [];
    const seen = new Set();
    const out = [];
    for (const m of matches) {
      const key = String(m).toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(m);
    }
    return out;
  };

  const joinEmails = (emails) => {
    const list = Array.isArray(emails) ? emails.filter(Boolean) : [];
    return list.join(', ');
  };

  const buildReplySubject = (subject) => {
    const s = cleanSubject(subject || '');
    if (!s) return 'Re:';
    if (/^re:\s*/i.test(s)) return s;
    return `Re: ${s}`;
  };

  const buildForwardSubject = (subject) => {
    const s = cleanSubject(subject || '');
    if (!s) return 'Fwd:';
    if (/^(fwd:|fw:)\s*/i.test(s)) return s;
    return `Fwd: ${s}`;
  };

  const isSentFolderPath = (path) => {
    const p = String(path || '').toLowerCase();
    return p.includes('sent') || p.includes('inviata') || p.includes('inviati');
  };

  const formatAccountLabel = (value) => {
    const v = String(value || '').trim();
    if (!v) return '';
    const at = v.indexOf('@');
    if (at > 0) return `${v.slice(0, at)}@`;
    return v;
  };

  const formatTimeHHMM = (dateValue) => {
    if (!dateValue) return '';
    const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  };

  const formatListDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const startOfDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (startOfDate.getTime() === startOfToday.getTime()) {
      return formatTimeHHMM(d);
    }
    if (startOfDate.getTime() === startOfYesterday.getTime()) {
      return 'ieri';
    }
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString('it-IT', sameYear ? { day: '2-digit', month: 'short' } : { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const cleanSubject = (subject) => {
    if (!subject) return '(Nessun oggetto)';
    return subject.replace(/^POSTA CERTIFICATA:\s*/i, '');
  };

  const isPecMessage = (subject) => {
    return subject && subject.toUpperCase().includes('POSTA CERTIFICATA:');
  };

  const handleFolderClick = (folder) => {
    setCurrentFolder(folder);
    setPage(0);
    setSelectedEmail(null);
  };

  const handleOpenDayFilter = (event) => {
    setDayFilterAnchorEl(event?.currentTarget || null);
    setDayFilterDraft(dayFilter || new Date().toISOString().slice(0, 10));
  };

  const handleCloseDayFilter = () => {
    setDayFilterAnchorEl(null);
  };

  const handleApplyDayFilter = () => {
    const value = String(dayFilterDraft || '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
    setDayFilter(value);
    setPage(0);
    const dayOffsetStart = getTimezoneOffsetForDayStart(value, 0);
    const dayOffsetEnd = getTimezoneOffsetForDayStart(value, 1);
    fetchEmails({
      page: 1,
      day: value,
      ...(dayOffsetStart != null ? { dayOffsetStart } : {}),
      ...(dayOffsetEnd != null ? { dayOffsetEnd } : {})
    });
    handleCloseDayFilter();
  };

  const handleClearDayFilter = () => {
    setDayFilter('');
    setDayFilterDraft('');
    setPage(0);
    fetchEmails({ page: 1, day: '' });
    handleCloseDayFilter();
  };

  const previewKey = (uid, folder) => `${folder || ''}:${uid || ''}`;

  const prefetchBodyPreviews = async (items, folder) => {
    if (bodyPreviewCancelRef.current) {
      try {
        bodyPreviewCancelRef.current();
      } catch {}
    }
    const list = Array.isArray(items) ? items : [];
    if (!list.length) return;
    const shouldPrefetch = folder && (String(folder).toLowerCase() === 'inbox' || isSentFolderPath(folder));
    if (!shouldPrefetch) return;

    const targets = list
      .filter((e) => e && e.uid != null)
      .map((e) => ({ uid: e.uid, folder }))
      .filter(({ uid, folder: f }) => !bodyPreviewCacheRef.current[previewKey(uid, f)]);

    if (!targets.length) return;

    let cancelled = false;
    bodyPreviewCancelRef.current = () => {
      cancelled = true;
    };
    const limit = 3;
    let cursor = 0;

    const worker = async () => {
      while (!cancelled) {
        const idx = cursor;
        cursor += 1;
        if (idx >= targets.length) return;
        const { uid, folder: f } = targets[idx];
        try {
          const res = await api.get(`/email-accounts/emails/body`, {
            params: { account_id: accountId, uid, folder: f }
          });
          const raw = String(res?.data?.text || '').replace(/\s+/g, ' ').trim();
          const snippet = raw ? raw.slice(0, 140) : '';
          bodyPreviewCacheRef.current[previewKey(uid, f)] = snippet;
          setBodyPreviewByUid((prev) => {
            const k = previewKey(uid, f);
            if (prev[k]) return prev;
            return { ...prev, [k]: snippet };
          });
        } catch (e) {
          bodyPreviewCacheRef.current[previewKey(uid, f)] = '';
          setBodyPreviewByUid((prev) => {
            const k = previewKey(uid, f);
            if (prev[k]) return prev;
            return { ...prev, [k]: '' };
          });
        }
      }
    };

    const workers = Array.from({ length: Math.min(limit, targets.length) }, () => worker());
    await Promise.all(workers);
  };

  useEffect(() => {
    syncStatusRef.current = syncStatus;
  }, [syncStatus]);


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const [showOriginal, setShowOriginal] = useState(false);

  const handleEmailClick = async (email) => {
    setSelectedEmail(email);
    setLoadingBody(true);
    setLoadingReceipts(true);
    setEmailBody(null);
    setReceipts([]);
    setShowOriginal(false); // Reset view mode
    
    // 1. Fetch Body
    api.get(`/email-accounts/${accountId}/emails/${email.uid}/body`, {
          params: { folder: currentFolder, mark_seen: 1 }
        })
        .then(response => {
            let { text, html } = response.data;
            const originalText = text;
            const originalHtml = html;
            
            // Clean PEC boilerplate
            const pecPatterns = [
                // Aruba/Standard PEC headers - Regex for text
                /Messaggio di posta certificata[\s\S]*?Identificativo messaggio:.*(\r\n|\n)/gi,
                /Certified mail message[\s\S]*?Message identifier:.*(\r\n|\n)/gi,
                /Posta Certificata:[\s\S]*?(\r\n|\n)/gi,
                // Common headers block in forwarded PECs (From...To)
                /From:\s*.*?(\r\n|\n)Subject:\s*.*?(\r\n|\n)Date:\s*.*?(\r\n|\n)To:\s*.*?(\r\n|\n)/gi
            ];
            
            if (text) {
                pecPatterns.forEach(p => { text = text.replace(p, ''); });
                text = text.trim();
            }
            
            if (html) {
                // ... (HTML cleaning logic kept minimal)
            }
            
            // Safety check
            if (!text && originalText) text = originalText;

            setEmailBody({
                ...response.data,
                text,
                html,
                originalText,
                originalHtml
            });

            setEmails((prev) =>
              prev.map((e) => (e.uid === email.uid ? { ...e, is_read: true } : e))
            );
            refreshFolderCounts();
        })
        .catch(error => {
            console.error('Error fetching email body:', error);
            toast.error('Errore nel recupero del contenuto');
        })
        .finally(() => {
            setLoadingBody(false);
        });

    // 2. Fetch Receipts (Parallel)
    api.get(`/email-accounts/${accountId}/emails/${email.uid}/receipts`, {
        params: { folder: currentFolder }
    })
    .then(res => {
        setReceipts(res.data || []);
    })
    .catch(err => {
        console.error('Error fetching receipts:', err);
    })
    .finally(() => {
        setLoadingReceipts(false);
    });
  };

  const isTechnicalAttachment = (filename) => {
      if (!filename) return false;
      const f = filename.toLowerCase();
      return f === 'daticert.xml' || 
             f === 'smime.p7s' || 
             f === 'postacert.eml' ||
             f.endsWith('.p7m') || 
             f === 'oledata.mso';
  };
  
  const handleDownloadAttachment = async (idx, filename) => {
    try {
      const response = await api.get(`/email-accounts/${accountId}/emails/${selectedEmail.uid}/attachments/${idx}/download`, {
        params: { folder: currentFolder },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'allegato';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore download allegato:', error);
      toast.error('Errore nel download dell\'allegato');
    }
  };

  const handleDownloadHistoryAttachment = async (idx, filename) => {
    try {
      if (!historyDialog.email) return;
      const response = await api.get(`/email-accounts/${accountId}/emails/${historyDialog.email.uid}/attachments/${idx}/download`, {
        params: { folder: currentFolder },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'allegato';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Errore download allegato (cronologia):', error);
      toast.error('Errore nel download dell\'allegato');
    }
  };

  const downloadSelectedEmailAttachmentAsFile = async (idx, filename) => {
    const response = await api.get(`/email-accounts/${accountId}/emails/${selectedEmail.uid}/attachments/${idx}/download`, {
      params: { folder: currentFolder },
      responseType: 'blob'
    });
    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/octet-stream' });
    const name = filename || 'allegato';
    return new File([blob], name, { type: blob.type || 'application/octet-stream', lastModified: Date.now() });
  };

  const loadSelectedEmailAttachmentsIntoCompose = async (options = {}) => {
    const includeTechnical = Boolean(options.includeTechnical);
    if (!selectedEmail || !emailBody?.attachments || !Array.isArray(emailBody.attachments) || emailBody.attachments.length === 0) return;

    const listWithIndex = emailBody.attachments
      .map((att, idx) => ({ att, idx }))
      .filter(({ att }) => includeTechnical || !isTechnicalAttachment(att?.filename));

    if (listWithIndex.length === 0) return;

    setComposeOriginalAttachmentsLoading(true);
    try {
      const tasks = listWithIndex.map(({ att, idx }) => downloadSelectedEmailAttachmentAsFile(idx, att?.filename));

      const results = await Promise.allSettled(tasks);
      const okFiles = results.filter((r) => r.status === 'fulfilled').map((r) => r.value);
      const failCount = results.filter((r) => r.status === 'rejected').length;

      if (okFiles.length) {
        setComposeData((prev) => ({ ...prev, attachments: [...prev.attachments, ...okFiles] }));
      }
      if (failCount) {
        toast.warning(`Alcuni allegati non sono stati caricati (${failCount}).`);
      }
    } catch (e) {
      toast.error('Errore durante il caricamento degli allegati.');
    } finally {
      setComposeOriginalAttachmentsLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    const b = Number(bytes);
    if (!Number.isFinite(b) || b < 0) return '';
    if (b < 1024) return `${b} B`;
    const kb = b / 1024;
    if (kb < 1024) return `${Math.round(kb * 10) / 10} KB`;
    const mb = kb / 1024;
    if (mb < 1024) return `${Math.round(mb * 10) / 10} MB`;
    const gb = mb / 1024;
    return `${Math.round(gb * 10) / 10} GB`;
  };

  const isHistoryAttachmentPreviewSupported = (att) => {
    const mime = String(att?.content_type || '').toLowerCase();
    const filename = String(att?.filename || '').toLowerCase();
    const ext = filename.includes('.') ? filename.split('.').pop() : '';
    if (mime === 'application/pdf' || ext === 'pdf') return true;
    if (mime.startsWith('image/')) return true;
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return true;
    return false;
  };

  const loadHistoryAttachmentPreview = async (att) => {
    if (!att || att.index == null) return;
    if (!historyDialog.email) return;
    if (!isHistoryAttachmentPreviewSupported(att)) {
      setHistoryPreviewLoading(false);
      setHistoryPreviewUrl('');
      setHistoryPreviewType('');
      setHistoryPreviewError('Anteprima non disponibile per questo tipo di file');
      return;
    }
    if (historyPreviewUrl) {
      try {
        window.URL.revokeObjectURL(historyPreviewUrl);
      } catch {}
    }
    setHistoryPreviewLoading(true);
    setHistoryPreviewError('');
    setHistoryPreviewUrl('');
    setHistoryPreviewType('');
    try {
      const response = await api.get(
        `/email-accounts/${accountId}/emails/${historyDialog.email.uid}/attachments/${att.index}/download`,
        { params: { folder: currentFolder }, responseType: 'blob' }
      );
      const type = response.headers['content-type'] || att.content_type || 'application/octet-stream';
      const blob = new Blob([response.data], { type });
      const url = window.URL.createObjectURL(blob);
      setHistoryPreviewType(type);
      setHistoryPreviewUrl(url);
    } catch (e) {
      setHistoryPreviewError(e?.response?.data?.message || 'Anteprima non disponibile');
    } finally {
      setHistoryPreviewLoading(false);
    }
  };

  const openHistoryAttachmentInNewTab = async (att) => {
    try {
      if (!att || att.index == null) return;
      if (!historyDialog.email) return;
      const response = await api.get(
        `/email-accounts/${accountId}/emails/${historyDialog.email.uid}/attachments/${att.index}/download`,
        { params: { folder: currentFolder }, responseType: 'blob' }
      );
      const type = response.headers['content-type'] || att.content_type || 'application/octet-stream';
      const blob = new Blob([response.data], { type });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => {
        try {
          window.URL.revokeObjectURL(url);
        } catch {}
      }, 60_000);
    } catch (e) {
      toast.error('Impossibile aprire l’allegato');
    }
  };

  const handleSelectHistoryAttachment = (att) => {
    setHistorySelectedAttachment(att);
    loadHistoryAttachmentPreview(att);
  };

  const handleDownloadAllHistoryAttachments = async () => {
    if (!Array.isArray(historyAttachments) || historyAttachments.length === 0) return;
    for (const att of historyAttachments) {
      await handleDownloadHistoryAttachment(att.index, att.filename);
    }
  };

  useEffect(() => {
    return () => {
      if (historyPreviewUrl) {
        try {
          window.URL.revokeObjectURL(historyPreviewUrl);
        } catch {}
      }
    };
  }, [historyPreviewUrl]);

  const getReceiptType = (subject) => {
      const s = subject.toLowerCase();
      if (s.includes('accettazione')) return 'accettazione';
      if (s.includes('consegna')) return 'consegna';
      if (s.includes('mancata consegna')) return 'errore';
      return 'altro';
  };

  const renderReceiptIcon = (type) => {
      switch(type) {
          case 'accettazione': return <CheckCircleIcon color="success" sx={{ fontSize: 20, color: '#4caf50' }} />;
          case 'consegna': return <CheckCircleIcon color="success" sx={{ fontSize: 20, color: '#4caf50' }} />;
          case 'errore': return <ErrorIcon color="error" sx={{ fontSize: 20 }} />;
          default: return <ScheduleIcon color="action" sx={{ fontSize: 20 }} />;
      }
  };

  const getAttachmentIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    switch(ext) {
      case 'xml': return <CodeIcon color="action" />;
      case 'eml': return <EmailIcon color="action" />;
      case 'p7s': 
      case 'p7m': return <LockIcon color="action" />;
      case 'pdf': return <DescriptionIcon color="error" />;
      default: return <AttachmentIcon color="action" />;
    }
  };

  const extractMessageId = (text) => {
      if (!text) return null;
      // Regex per trovare l'Identificativo Messaggio nel testo della PEC/Ricevuta
      // Es: "Identificativo messaggio: jpec1303... @pec.aruba.it"
      // Es: "Message identifier: jpec1303... @pec.aruba.it"
      const match = text.match(/(?:Identificativo messaggio|Message identifier):\s*([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+)/i);
      return match ? match[1] : null;
  };

  const renderReceiptBody = (body) => {
      // If we have HTML, use it but try to strip the outer container if needed
      // Actually, receipts from Aruba are often plain text wrapped in simple HTML or just plain text.
      // The user screenshot shows plain text with "--- Ricevuta di avvenuta consegna ---".
      // Let's try to format the text version nicely if HTML is not great or if we want to force the look.
      
      let content = body.html || body.text || '';
      let messageId = null;

      // Extract Message ID from the original raw text if available
      if (body.text) {
          messageId = extractMessageId(body.text);
      }
      
      // If it's the text version with dashes, let's format it
      // "--- Ricevuta di avvenuta consegna ---" -> <h3>Ricevuta di avvenuta consegna</h3>
      // "--- Delivery pec notification ---" -> <h3>Delivery pec notification</h3>
      
      if (!body.html && body.text) {
          // It's plain text, let's make it look like HTML
          content = body.text
            .replace(/^-+\s*(.*?)\s*-+$/gm, '<h3 style="color: #333; margin-top: 20px; margin-bottom: 10px; font-size: 1.1rem; border-bottom: 1px solid #eee; padding-bottom: 5px;">$1</h3>')
            .replace(/\n/g, '<br/>');
            
          // Linkify emails
          content = content.replace(
              /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/gi, 
              '<a href="mailto:$1" style="color: #1976d2; text-decoration: none;">$1</a>'
          );
      }

      return (
          <Box>
              <div dangerouslySetInnerHTML={{ __html: content }} style={{ fontFamily: 'Roboto, sans-serif', fontSize: '0.95rem', lineHeight: 1.6, color: '#333' }} />
              
              {messageId && (
                  <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #eee' }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                          Identificativo messaggio (ID Legale):
                      </Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: '#f5f5f5', p: 1, borderRadius: 1, display: 'inline-block', userSelect: 'all' }}>
                          {messageId}
                      </Typography>
                  </Box>
              )}
          </Box>
      );
  };

  const handleBackToStats = () => {
    navigate('/email-accounts');
  };

  const handleForceResync = async () => {
      setResyncDialogOpen(true);
  };

  const handleCloseResyncDialog = () => {
      if (resyncSubmitting) return;
      setResyncDialogOpen(false);
  };

  const handleOpenFolderSyncDialog = () => {
    setFolderSyncDialogOpen(true);
  };

  const handleCloseFolderSyncDialog = () => {
    if (folderSyncSubmitting) return;
    setFolderSyncDialogOpen(false);
  };

  const handleConfirmFolderSync = async () => {
    if (!accountId) return;
    setFolderSyncSubmitting(true);
    try {
      refreshAfterSyncRef.current = true;
      await api.post(`/email-accounts/${accountId}/sync`, { folder: currentFolder });
      toast.info(`Sincronizzazione avviata (cartella: ${getDisplayName(currentFolder)})…`);
      setFolderSyncDialogOpen(false);
    } catch (e) {
      refreshAfterSyncRef.current = false;
      toast.error('Errore durante l’avvio della sincronizzazione');
    } finally {
      setFolderSyncSubmitting(false);
      setPage(0);
      fetchEmails({ page: 1 });
    }
  };

  const handleConfirmResync = async () => {
      setResyncSubmitting(true);
      try {
          await api.post(`/email-accounts/${accountId}/sync`, { folder: '__ALL__' });
          setResyncDialogOpen(false);
          toast.info('Risincronizzazione avviata (tutte le cartelle, ultimi 12 mesi). Le email appariranno man mano.');
      } catch (error) {
          console.error('Errore avvio sync:', error);
          toast.error('Errore durante l\'avvio della sincronizzazione');
      } finally {
          setResyncSubmitting(false);
      }
  };

  const handleDeleteEmail = async () => {
      if (!selectedEmail) return;
      if (!window.confirm('Confermi l’eliminazione di questo messaggio dall’archivio locale? L’operazione è irreversibile.')) return;
      
      try {
          await api.delete(`/email-accounts/${accountId}/emails/${selectedEmail.uid}`, {
              params: { folder: currentFolder }
          });
          toast.success('Email eliminata');
          // Update list
          setEmails(prev => prev.filter(e => e.uid !== selectedEmail.uid));
          setSelectedEmail(null);
      } catch (error) {
          console.error('Error deleting email:', error);
          toast.error('Errore durante l\'eliminazione');
      }
  };

  if (loadingAccount) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!account) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">Account non trovato</Typography>
        <Button onClick={handleBackToStats}>Torna indietro</Button>
      </Box>
    );
  }

  // Helper to determine folder priority for sorting
  const getFolderPriority = (path) => {
    const p = path.toLowerCase();
    if (p === 'inbox') return 0; // Strict check for INBOX
    if (p.includes('sent') || p.includes('inviata') || p.includes('inviati')) return 1;
    if (p.includes('draft') || p.includes('bozze')) return 2;
    if (p.includes('spam') || p.includes('junk') || p.includes('indesiderata')) return 3;
    if (p.includes('trash') || p.includes('cestino') || p.includes('bin')) return 4;
    return 5;
  };

  // Helper to get display name
  const getDisplayName = (path) => {
    const p = path.toLowerCase();
    if (p === 'inbox') return 'In arrivo';
    if (p.includes('sent') || p.includes('inviata') || p.includes('inviati')) return 'Inviati';
    if (p.includes('draft') || p.includes('bozze')) return 'Bozze';
    if (p.includes('spam') || p.includes('junk') || p.includes('indesiderata')) return 'Spam';
    if (p.includes('trash') || p.includes('cestino') || p.includes('bin')) return 'Cestino';
    
    // Default: strip prefixes
    return path.replace(/^INBOX\./i, '').replace(/^\[Gmail\]\//i, '');
  };

  const getReadFilterMeta = (value) => {
    if (value === 'unread') return { label: 'Non letti', icon: <MailIcon sx={{ fontSize: 18, color: 'info.main' }} /> };
    if (value === 'read') return { label: 'Letti', icon: <DraftsIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> };
    return { label: 'Tutti', icon: <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> };
  };

  const getWorkflowFilterMeta = (value) => {
    if (value === 'unassigned') return { label: 'Da assegnare', icon: <AssignIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> };
    if (value === 'assigned') return { label: 'Assegnati', icon: <AssignIcon sx={{ fontSize: 18, color: 'info.main' }} /> };
    if (value === 'assigned_to_me') return { label: 'Assegnate a me', icon: <ClaimIcon sx={{ fontSize: 18, color: 'success.main' }} /> };
    if (value === 'closed') return { label: 'Risolte / chiuse', icon: <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} /> };
    if (value === 'non_gestire') return { label: 'Non gestire', icon: <NonGestireIcon sx={{ fontSize: 18, color: 'error.main' }} /> };
    return { label: 'Tutti', icon: <AssignIcon sx={{ fontSize: 18, color: 'text.secondary' }} /> };
  };

  const formatDurationShort = (ms) => {
    const safe = Number(ms);
    if (!Number.isFinite(safe) || safe < 0) return '—';
    const totalMinutes = Math.round(safe / 60000);
    if (totalMinutes < 1) return '<1 min';
    if (totalMinutes < 60) return `${totalMinutes} min`;
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return m ? `${h}h ${m}min` : `${h}h`;
  };

  const getHistoryOperatorLabel = () => {
    const events = Array.isArray(historyData?.events) ? historyData.events : [];
    const claim = events.find((e) => e && e.tipo_evento === 'preso_in_carico');
    const best = claim || events[0] || null;
    const name = best?.operatore_nome || best?.operatore_email || null;
    return name || user?.name || user?.email || 'Operatore';
  };

  const getHistoryComputedSummary = () => {
    const receivedAtRaw = historyDialog?.email?.date || null;
    const receivedAt = receivedAtRaw ? new Date(receivedAtRaw) : null;
    const receivedMs = receivedAt && !Number.isNaN(receivedAt.getTime()) ? receivedAt.getTime() : null;

    const events = Array.isArray(historyData?.events) ? historyData.events : [];
    const interventionTypes = new Set(['preso_in_carico', 'nota_aggiunta', 'cambio_stato', 'assegnato', 'riassegnato']);
    let firstInterventionMs = null;
    for (const ev of events) {
      if (!ev || !interventionTypes.has(ev.tipo_evento)) continue;
      const d = ev.creato_il ? new Date(ev.creato_il) : null;
      const t = d && !Number.isNaN(d.getTime()) ? d.getTime() : null;
      if (t == null) continue;
      if (firstInterventionMs == null || t < firstInterventionMs) firstInterventionMs = t;
    }

    const closeAtRaw = historyData?.email?.chiuso_il || null;
    const closeAt = closeAtRaw ? new Date(closeAtRaw) : null;
    const closeMs = closeAt && !Number.isNaN(closeAt.getTime()) ? closeAt.getTime() : null;

    return {
      firstInterventionDelay:
        receivedMs != null && firstInterventionMs != null ? formatDurationShort(firstInterventionMs - receivedMs) : '—',
      closedDelay: receivedMs != null && closeMs != null ? formatDurationShort(closeMs - receivedMs) : '—'
    };
  };

  const sortedFolders = [...folders].sort((a, b) => {
    const pathA = typeof a === 'string' ? a : a.path;
    const pathB = typeof b === 'string' ? b : b.path;
    return getFolderPriority(pathA) - getFolderPriority(pathB);
  });

  const getFolderVisual = (path) => {
    const p = String(path || '').toLowerCase();
    if (p === 'inbox' || p === 'in arrivo') return { Icon: InboxIcon, palette: 'info' };
    if (p.includes('sent') || p.includes('inviata') || p.includes('inviati')) return { Icon: SendIcon, palette: 'primary' };
    if (p.includes('draft') || p.includes('bozze')) return { Icon: EditIcon, palette: 'warning' };
    if (p.includes('spam') || p.includes('junk') || p.includes('indesiderata')) return { Icon: SpamIcon, palette: 'secondary' };
    if (p.includes('trash') || p.includes('cestino') || p.includes('bin')) return { Icon: DeleteIcon, palette: 'error' };
    if (p.includes('template')) return { Icon: DescriptionIcon, palette: 'secondary' };
    return { Icon: MailIcon, palette: 'success' };
  };

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, height: 'calc(100vh - 120px)', width: '100%', overflow: 'hidden' }}>
      {/* Sidebar / Folder List (Simplified) */}
      <Paper sx={{ width: 240, flexShrink: 0, mr: 2, display: { xs: 'none', md: 'block' }, overflowY: 'auto', height: '100%' }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handleBackToStats} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {account?.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, mt: 0.25 }}>
              <Box
                sx={(theme) => ({
                  width: 20,
                  height: 20,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.18)
                })}
              >
                <MailIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              </Box>
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
                {account?.email || '—'}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Divider />
        <List>
          {loadingFolders ? (
            <ListItem>
                <ListItemIcon><CircularProgress size={20} /></ListItemIcon>
                <ListItemText primary="Caricamento..." />
            </ListItem>
          ) : folderError ? (
            <ListItem>
                <ListItemIcon><DeleteIcon color="error" /></ListItemIcon>
                <ListItemText primary="Errore" secondary={folderError} />
            </ListItem>
          ) : sortedFolders.length > 0 ? (
            sortedFolders.map((folderObj) => {
              // Handle both string (legacy/fallback) and object structure
              const path = typeof folderObj === 'string' ? folderObj : folderObj.path;
              const total = typeof folderObj === 'string' ? 0 : folderObj.total;
              const unread = typeof folderObj === 'string' ? 0 : folderObj.unread;

              const displayName = getDisplayName(path);
              const { Icon, palette } = getFolderVisual(path);

              return (
                <ListItem 
                  button 
                  key={path}
                  selected={currentFolder === path}
                  onClick={() => handleFolderClick(path)}
                  sx={{
                    borderRadius: 1,
                    mx: 1,
                    my: 0.25,
                    '&.Mui-selected': { bgcolor: 'action.selected' },
                    '&.Mui-selected:hover': { bgcolor: 'action.selected' }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 42 }}>
                    <Box
                      sx={(theme) => ({
                        width: 30,
                        height: 30,
                        borderRadius: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: alpha(theme.palette[palette].main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                        border: '1px solid',
                        borderColor: alpha(theme.palette[palette].main, theme.palette.mode === 'dark' ? 0.35 : 0.18)
                      })}
                    >
                      <Icon sx={{ fontSize: 18, color: `${palette}.main` }} />
                    </Box>
                  </ListItemIcon>
                  <Tooltip title={total > 0 ? `Totale: ${total}` : ''} disableHoverListener={!(total > 0)}>
                    <ListItemText primary={displayName} />
                  </Tooltip>
                  {unread > 0 && (
                    <Chip
                      label={unread}
                      size="small"
                      variant="outlined"
                      sx={(theme) => ({
                        height: 20,
                        minWidth: 24,
                        fontSize: '0.75rem',
                        borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.45 : 0.25),
                        color: 'info.main',
                        bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)
                      })}
                    />
                  )}
                </ListItem>
              );
            })
          ) : (
            // Fallback default folders if fetch fails or is slow
            <>
              <ListItem button selected={currentFolder === 'INBOX'} onClick={() => handleFolderClick('INBOX')}>
                <ListItemIcon sx={{ minWidth: 42 }}>
                  <Box
                    sx={(theme) => ({
                      width: 30,
                      height: 30,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                      border: '1px solid',
                      borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.18)
                    })}
                  >
                    <InboxIcon sx={{ fontSize: 18, color: 'info.main' }} />
                  </Box>
                </ListItemIcon>
                <ListItemText primary="Posta in arrivo (Default)" />
              </ListItem>
            </>
          )}
        </List>
      </Paper>

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
        
        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
           <TextField
            size="small"
            placeholder="Cerca email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && fetchEmails()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filtro</InputLabel>
            <Select
              value={filterType}
              label="Filtro"
              renderValue={(selected) => {
                const m = getReadFilterMeta(selected);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {m.icon}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {m.label}
                    </Typography>
                  </Box>
                );
              }}
              onChange={(e) => {
                const next = e.target.value;
                setFilterType(next);
                setPage(0);
                fetchEmails({ page: 1, filter: next, workflowFilter });
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getReadFilterMeta('all').icon}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Tutti
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="unread">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getReadFilterMeta('unread').icon}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Non letti
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="read">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getReadFilterMeta('read').icon}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Letti
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 190 }}>
            <InputLabel>Workflow</InputLabel>
            <Select
              value={workflowFilter}
              label="Workflow"
              renderValue={(selected) => {
                const m = getWorkflowFilterMeta(selected);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {m.icon}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {m.label}
                    </Typography>
                  </Box>
                );
              }}
              onChange={(e) => {
                const next = e.target.value;
                setWorkflowFilter(next);
                setPage(0);
                fetchEmails({ page: 1, workflowFilter: next, filter: filterType });
              }}
            >
              <MenuItem value="all">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getWorkflowFilterMeta('all').icon}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Tutti
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="unassigned">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getWorkflowFilterMeta('unassigned').icon}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Da assegnare
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="assigned">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getWorkflowFilterMeta('assigned').icon}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Assegnati
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="assigned_to_me">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getWorkflowFilterMeta('assigned_to_me').icon}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Assegnate a me
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="closed">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getWorkflowFilterMeta('closed').icon}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Risolte / chiuse
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="non_gestire">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  {getWorkflowFilterMeta('non_gestire').icon}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Non gestire
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {dayFilter && (
            <Tooltip title="Filtro giorno attivo (clicca X per disattivare)">
              <Chip
                icon={<CalendarIcon sx={{ color: 'info.main' }} />}
                label={`Giorno: ${formatItalianDay(dayFilter)}`}
                onDelete={handleClearDayFilter}
                deleteIcon={<CloseIcon sx={{ color: 'info.main' }} />}
                variant="outlined"
                sx={(theme) => ({
                  maxWidth: 220,
                  borderRadius: 1,
                  borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.55 : 0.30),
                  bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
                  color: 'text.primary',
                  '& .MuiChip-label': { fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }
                })}
              />
            </Tooltip>
          )}

          <Tooltip title={dayFilter ? `Filtro giorno: ${formatItalianDay(dayFilter)}` : 'Filtro per giorno'}>
            <IconButton
              onClick={handleOpenDayFilter}
              size="small"
              sx={(theme) => ({
                width: 38,
                height: 38,
                borderRadius: 1,
                border: '1px solid',
                borderColor: dayFilter
                  ? alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.55 : 0.35)
                  : 'divider',
                bgcolor: dayFilter
                  ? alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.16 : 0.08)
                  : theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.04)'
                    : 'rgba(0,0,0,0.02)',
                color: dayFilter ? 'info.main' : 'text.secondary',
                '&:hover': {
                  bgcolor: dayFilter
                    ? alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.24 : 0.12)
                    : theme.palette.mode === 'dark'
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(0,0,0,0.06)'
                }
              })}
            >
              <CalendarIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Aggiorna">
            <IconButton
              onClick={handleOpenFolderSyncDialog}
              size="small"
              sx={(theme) => ({
                width: 38,
                height: 38,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
              })}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Nuovo messaggio">
            <IconButton
              onClick={handleComposeOpen}
              size="small"
              sx={(theme) => ({
                width: 38,
                height: 38,
                borderRadius: 1,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                color: 'primary.main',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.26 : 0.16) }
              })}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Risincronizzazione completa">
            <IconButton
              onClick={handleForceResync}
              size="small"
              sx={(theme) => ({
                width: 38,
                height: 38,
                borderRadius: 1,
                border: '1px solid',
                borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.55 : 0.35),
                bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.10 : 0.06),
                color: 'warning.main',
                '&:hover': { bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) }
              })}
            >
              <SyncIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Paper>

        <Popover
          open={Boolean(dayFilterAnchorEl)}
          anchorEl={dayFilterAnchorEl}
          onClose={handleCloseDayFilter}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { borderRadius: 1, p: 0.5 } }}
        >
          <Box sx={{ p: 1.5, width: 280 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
              <Box
                sx={(theme) => ({
                  width: 32,
                  height: 32,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                  border: '1px solid',
                  borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.18)
                })}
              >
                <CalendarIcon sx={{ fontSize: 18, color: 'info.main' }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                  Filtro per giorno
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Mostra le email del giorno selezionato
                </Typography>
              </Box>
            </Box>

            <TextField
              label="Giorno"
              type="date"
              value={dayFilterDraft || ''}
              onChange={(e) => setDayFilterDraft(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mt: 1.5 }}>
              <Button
                color="inherit"
                variant="outlined"
                onClick={handleClearDayFilter}
                disabled={!dayFilter}
                startIcon={<CloseIcon />}
                sx={{ borderRadius: 1 }}
              >
                Disattiva
              </Button>
              <Button
                variant="contained"
                color="info"
                onClick={handleApplyDayFilter}
                disabled={!/^\d{4}-\d{2}-\d{2}$/.test(String(dayFilterDraft || ''))}
                sx={{ borderRadius: 1 }}
              >
                Applica
              </Button>
            </Box>
          </Box>
        </Popover>

        <Dialog
          open={resyncDialogOpen}
          onClose={handleCloseResyncDialog}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={resyncSubmitting}
        >
          <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={(theme) => ({
                width: 38,
                height: 38,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                border: '1px solid',
                borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.35 : 0.18)
              })}
            >
              <ErrorIcon sx={{ color: 'warning.main' }} fontSize="small" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 700 }}>
                Risincronizzazione completa
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {account?.email || account?.name || 'Account email'}
              </Typography>
            </Box>
            {resyncSubmitting && (
              <Chip
                label="Avvio in corso…"
                color="info"
                size="small"
                icon={<CircularProgress size={14} />}
                sx={{ ml: 'auto' }}
              />
            )}
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5 }}>
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 1,
                borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.40 : 0.25),
                bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.08 : 0.06),
                mb: 2
              })}
            >
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <ErrorIcon sx={{ color: 'warning.main', mt: '2px' }} fontSize="small" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                    Operazione avanzata
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Avvia una risincronizzazione di tutte le cartelle (finestra ultimi 12 mesi e con limiti per batch). Può richiedere tempo su caselle grandi.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1,
                borderColor: 'divider',
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.06)'
                    : 'rgba(0, 0, 0, 0.04)'
              }}
            >
              <Box sx={{ display: 'grid', gap: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mt: '2px' }} fontSize="small" />
                  <Typography variant="body2">Continua a lavorare: la sincronizzazione procede in background.</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <ScheduleIcon sx={{ color: 'warning.main', mt: '2px' }} fontSize="small" />
                  <Typography variant="body2">Su caselle con migliaia di email può richiedere diversi minuti e potrebbe completare in più passaggi.</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <LockIcon sx={{ color: 'info.main', mt: '2px' }} fontSize="small" />
                  <Typography variant="body2">Se una sincronizzazione è già in corso, la richiesta viene gestita in modo sicuro.</Typography>
                </Box>
              </Box>
            </Paper>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={handleCloseResyncDialog} disabled={resyncSubmitting} color="inherit" startIcon={<CloseIcon />}>
              Annulla
            </Button>
            <Button
              onClick={handleConfirmResync}
              variant="contained"
              color="warning"
              disabled={resyncSubmitting}
              startIcon={resyncSubmitting ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            >
              Avvia risincronizzazione
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={folderSyncDialogOpen}
          onClose={handleCloseFolderSyncDialog}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={folderSyncSubmitting}
        >
          <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={(theme) => ({
                width: 38,
                height: 38,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                border: '1px solid',
                borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.18)
              })}
            >
              <RefreshIcon sx={{ color: 'info.main' }} fontSize="small" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 700 }}>
                Aggiorna cartella
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {account?.email || account?.name || 'Account email'} • {getDisplayName(currentFolder)}
              </Typography>
            </Box>
            {folderSyncSubmitting && (
              <Chip
                label="Avvio in corso…"
                color="info"
                size="small"
                icon={<CircularProgress size={14} />}
                sx={{ ml: 'auto' }}
              />
            )}
          </DialogTitle>
          <DialogContent sx={{ pt: 2.5 }}>
            <Paper
              variant="outlined"
              sx={(theme) => ({
                p: 1.5,
                borderRadius: 1,
                borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.40 : 0.25),
                bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.08 : 0.06),
                mb: 2
              })}
            >
              <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <InfoIcon sx={{ color: 'info.main', mt: '2px' }} fontSize="small" />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                    Cosa fa
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Avvia la sincronizzazione della sola cartella selezionata (es. In arrivo, Inviati). Le email già indicizzate restano disponibili subito.
                  </Typography>
                </Box>
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 1,
                borderColor: 'divider',
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.06)'
                    : 'rgba(0, 0, 0, 0.04)'
              }}
            >
              <Box sx={{ display: 'grid', gap: 1.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: 'success.main', mt: '2px' }} fontSize="small" />
                  <Typography variant="body2">Prosegue in background: puoi continuare a lavorare.</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <ScheduleIcon sx={{ color: 'warning.main', mt: '2px' }} fontSize="small" />
                  <Typography variant="body2">Può richiedere alcuni minuti in base al volume.</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <AttachmentIcon sx={{ color: 'info.main', mt: '2px' }} fontSize="small" />
                  <Typography variant="body2">Quando presenti, gli allegati vengono archiviati su storage (Wasabi) e resi scaricabili.</Typography>
                </Box>
              </Box>
            </Paper>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button onClick={handleCloseFolderSyncDialog} disabled={folderSyncSubmitting} color="inherit" startIcon={<CloseIcon />}>
              Annulla
            </Button>
            <Button
              onClick={handleConfirmFolderSync}
              variant="contained"
              color="info"
              disabled={folderSyncSubmitting}
              startIcon={folderSyncSubmitting ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            >
              Avvia aggiornamento
            </Button>
          </DialogActions>
        </Dialog>
        
        {syncStatus === 'running' && (
          <Box sx={{ mb: 2 }}>
            <Paper sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
              {syncProgress && syncProgress.total > 0 ? (
                <>
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (syncProgress.processed / syncProgress.total) * 100)}
                      sx={(theme) => {
                        const tailTotal = Number(syncProgress?.tail?.total || 0);
                        const newTotal = Number(syncProgress?.new?.total || 0);
                        const mode = String(syncProgress?.mode || '').toLowerCase();
                        const barColor =
                          newTotal > 0
                            ? theme.palette.success.main
                            : tailTotal > 0
                              ? theme.palette.info.main
                              : (mode === 'since' || mode === 'resync')
                                ? theme.palette.primary.main
                                : theme.palette.info.main;
                        return {
                          height: 10,
                          borderRadius: 999,
                          backgroundColor:
                            theme.palette.mode === 'dark'
                              ? 'rgba(255,255,255,0.10)'
                              : 'rgba(0,0,0,0.08)',
                          '& .MuiLinearProgress-bar': {
                            borderRadius: 999,
                            backgroundColor: barColor
                          }
                        };
                      }}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {(() => {
                      const tailTotal = Number(syncProgress?.tail?.total || 0);
                      const tailProcessed = Number(syncProgress?.tail?.processed || 0);
                      const newTotal = Number(syncProgress?.new?.total || 0);
                      const newProcessed = Number(syncProgress?.new?.processed || 0);
                      const mode = String(syncProgress?.mode || '').toLowerCase();
                      const pct =
                        syncProgress?.total
                          ? Math.min(100, (Number(syncProgress.processed || 0) / Number(syncProgress.total || 1)) * 100)
                          : 0;
                      const pctText = ` ${Math.round(pct)}%`;

                      if (newTotal > 0 && tailTotal > 0) {
                        const newLabel = mode === 'since' || mode === 'resync' ? 'email indicizzate' : 'nuove email';
                        return `${newProcessed}/${newTotal} ${newLabel} • ${tailProcessed}/${tailTotal} controllate •${pctText}`;
                      }
                      if (newTotal > 0) {
                        const newLabel = mode === 'since' || mode === 'resync' ? 'email indicizzate' : 'nuove email';
                        return `${newProcessed}/${newTotal} ${newLabel} •${pctText}`;
                      }
                      if (tailTotal > 0) {
                        return `${tailProcessed}/${tailTotal} email controllate •${pctText}`;
                      }
                      return `${syncProgress.processed}/${syncProgress.total} •${pctText}`;
                    })()}
                  </Typography>
                </>
              ) : (
                <>
                  <LinearProgress sx={{ flexGrow: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    Sincronizzazione in corso…
                  </Typography>
                </>
              )}
            </Paper>
          </Box>
        )}
        {syncStatus === 'error' && (
          <Box
            sx={{
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 999,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Chip
              label="Errore sincronizzazione"
              color="error"
              variant="outlined"
              sx={{ borderRadius: 999, fontSize: '0.75rem', height: 26 }}
            />
            <Typography variant="body2" color="text.secondary">
              {syncError || 'Riprovare con Aggiorna'}
            </Typography>
          </Box>
        )}

        {/* Email List or Detail View */}
        <Paper sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {selectedEmail ? (
            // Email Detail View
            <Box key={selectedEmail.uid} sx={{ p: 3, flexGrow: 1, overflow: 'auto', height: '100%' }}>
              <Button startIcon={<ArrowBackIcon />} onClick={() => setSelectedEmail(null)} sx={{ mb: 2 }}>
                Torna all'elenco
              </Button>
              <Typography variant="h5" gutterBottom>{selectedEmail.subject}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, color: 'text.secondary' }}>
                <Typography variant="subtitle2">
                  {isSentFolderPath(currentFolder) ? `A: ${cleanRecipientName(selectedEmail.to)}` : `Da: ${cleanSenderName(selectedEmail.from)}`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="caption">{new Date(selectedEmail.date).toLocaleString()}</Typography>

                    <IconButton
                        onClick={handleReplyOpen}
                        title="Rispondi"
                        color="primary"
                        size="small"
                    >
                        <ReplyIcon />
                    </IconButton>

                    <IconButton
                        onClick={handleReplyAllOpen}
                        title="Rispondi a tutti"
                        color="primary"
                        size="small"
                    >
                        <ReplyAllIcon />
                    </IconButton>

                    <IconButton
                        onClick={handleForwardOpen}
                        title="Inoltra"
                        color="primary"
                        size="small"
                    >
                        <ForwardIcon />
                    </IconButton>
                    
                    <IconButton 
                        onClick={handleDeleteEmail} 
                        title="Elimina email" 
                        color="error"
                        size="small"
                    >
                        <DeleteIcon />
                    </IconButton>

                    {isPecMessage(selectedEmail.subject) && (
                        <Button 
                            size="small" 
                            onClick={() => setShowOriginal(!showOriginal)}
                            sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0 }}
                        >
                            {showOriginal ? 'Nascondi originale' : 'Mostra originale'}
                        </Button>
                    )}
                </Box>
              </Box>
              <Divider sx={{ mb: 3 }} />

              {/* Receipts Section */}
              {(loadingReceipts || receipts.length > 0) && (
                  <Box sx={{ mb: 3 }}>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          mb: 1, 
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                          py: 0.5,
                          px: 1,
                          borderRadius: 1
                        }}
                      >
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
                              Ricevute
                          </Typography>
                      </Box>
                      
                      <Paper variant="outlined" sx={{ overflow: 'hidden', borderColor: 'divider' }}>
                          {loadingReceipts ? (
                              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                  <CircularProgress size={20} />
                                  <Typography variant="body2" color="text.secondary">Ricerca ricevute in corso...</Typography>
                              </Box>
                          ) : (
                              <Table size="small">
                                  <TableHead>
                                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                                          <TableCell sx={{ fontWeight: 700, color: 'text.primary', borderBottom: '1px solid', borderBottomColor: 'divider', pl: 3 }}>Tipologia</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 700, color: 'text.primary', borderBottom: '1px solid', borderBottomColor: 'divider', pr: 3 }}>Data</TableCell>
                                      </TableRow>
                                  </TableHead>
                                  <TableBody>
                                      {receipts.map((receipt, index) => {
                                          const type = getReceiptType(receipt.subject);
                                          const label = type === 'accettazione' ? 'Ricevuta di accettazione' : 
                                                        type === 'consegna' ? 'Ricevuta di consegna' : 
                                                        type === 'errore' ? 'Errore di consegna' : 
                                                        cleanSubject(receipt.subject);
                                          
                                          return (
                                              <TableRow 
                                                  key={receipt.uid} 
                                                  sx={{ 
                                                      '&:last-child td': { borderBottom: 0 },
                                                      cursor: 'pointer',
                                                      '&:hover': { bgcolor: 'action.hover' }
                                                  }}
                                                  onClick={() => handleOpenReceipt(receipt)}
                                              >
                                                  <TableCell sx={{ py: 1.5, pl: 3 }}>
                                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                          {renderReceiptIcon(type)}
                                                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                              {label}
                                                          </Typography>
                                                      </Box>
                                                  </TableCell>
                                                  <TableCell align="right" sx={{ py: 1.5, pr: 3, color: 'text.secondary' }}>
                                                      {new Date(receipt.date).toLocaleDateString()}
                                                  </TableCell>
                                              </TableRow>
                                          );
                                      })}
                                  </TableBody>
                              </Table>
                          )}
                      </Paper>
                  </Box>
              )}
              
              {loadingBody ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <CircularProgress />
                  </Box>
              ) : emailBody ? (
                  <Box>
                      {/* Attachments section if needed */}
                      {emailBody.attachments && emailBody.attachments.length > 0 && (
                          <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {emailBody.attachments
                                .filter(att => showOriginal || !isTechnicalAttachment(att.filename))
                                .map((att, idx) => (
                                  <Chip 
                                      key={idx} 
                                      label={att.filename || 'Allegato'} 
                                      variant="outlined" 
                                      size="small"
                                      onClick={() => handleDownloadAttachment(idx, att.filename)}
                                  />
                              ))}
                          </Box>
                      )}

                      {/* Content */}
                      <Paper
                        variant="outlined"
                        sx={(theme) => ({
                          p: 2,
                          minHeight: 200,
                          borderColor: 'divider',
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'background.paper',
                          color: 'text.primary',
                          '& .email-body, & .email-body *': {
                            color: 'inherit !important'
                          },
                          '& .email-body a': {
                            color: `${theme.palette.primary.main} !important`
                          },
                          '& .email-body hr': {
                            borderColor: `${theme.palette.divider} !important`
                          },
                          '& .email-body blockquote': {
                            borderLeft: `3px solid ${theme.palette.divider}`,
                            marginLeft: 0,
                            paddingLeft: theme.spacing(1.5),
                            color: `${theme.palette.text.secondary} !important`
                          },
                          '& .email-body table, & .email-body td, & .email-body th': {
                            borderColor: `${theme.palette.divider} !important`
                          },
                          '& .email-body img': {
                            maxWidth: '100%',
                            height: 'auto'
                          }
                        })}
                      >
                          {showOriginal ? (
                              emailBody.originalHtml ? (
                                  <Box className="email-body" sx={{ fontFamily: 'inherit', fontSize: '0.95rem', lineHeight: 1.6 }}>
                                    <div dangerouslySetInnerHTML={{ __html: emailBody.originalHtml }} />
                                  </Box>
                              ) : (
                                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {emailBody.originalText || '(Nessun contenuto testuale)'}
                                  </Typography>
                              )
                          ) : (
                              emailBody.html ? (
                                  <Box className="email-body" sx={{ fontFamily: 'inherit', fontSize: '0.95rem', lineHeight: 1.6 }}>
                                    <div dangerouslySetInnerHTML={{ __html: emailBody.html }} />
                                  </Box>
                              ) : (
                                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                      {emailBody.text || '(Nessun contenuto testuale)'}
                                  </Typography>
                              )
                          )}
                      </Paper>
                  </Box>
              ) : (
                  <Typography variant="body2" color="text.secondary">
                      Seleziona un'email per visualizzarne il contenuto
                  </Typography>
              )}
            </Box>
          ) : (
            // Email List View
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 36 }} />
                      <TableCell sx={{ fontWeight: 800 }}>
                        <TableSortLabel
                          active={orderBy === (isSentFolderPath(currentFolder) ? 'to' : 'from')}
                          direction={orderBy === (isSentFolderPath(currentFolder) ? 'to' : 'from') ? order : 'asc'}
                          onClick={() => handleRequestSort(isSentFolderPath(currentFolder) ? 'to' : 'from')}
                        >
                          {isSentFolderPath(currentFolder) ? 'Destinatario / Oggetto' : 'Mittente / Oggetto'}
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: 140, fontWeight: 800 }}>
                        Account
                      </TableCell>
                      <TableCell sx={{ width: 260, fontWeight: 800 }}>
                        Workflow
                      </TableCell>
                      <TableCell align="right" sx={{ width: 110, fontWeight: 800 }}>
                        <TableSortLabel
                          active={orderBy === 'date'}
                          direction={orderBy === 'date' ? order : 'asc'}
                          onClick={() => handleRequestSort('date')}
                        >
                          Data
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <CircularProgress size={30} />
                        </TableCell>
                      </TableRow>
                    ) : emails.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography color="textSecondary">Nessuna email trovata</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      emails.map((email) => (
                        <TableRow 
                          key={email.uid} 
                          hover 
                          onClick={() => handleEmailClick(email)}
                          sx={{ 
                            cursor: 'pointer',
                            bgcolor: !email.is_read ? 'action.hover' : 'inherit',
                            fontWeight: !email.is_read ? 'bold' : 'normal',
                            '& .workflow-actions': {
                              opacity: 0,
                              pointerEvents: 'none',
                              transform: 'translateY(2px)',
                              transition: 'opacity 160ms ease, transform 160ms ease'
                            },
                            '& .workflow-default': {
                              opacity: 1,
                              transition: 'opacity 160ms ease'
                            },
                            '&:hover .workflow-actions': {
                              opacity: 1,
                              pointerEvents: 'auto',
                              transform: 'translateY(0)'
                            },
                            '&:hover .workflow-default': {
                              opacity: 0
                            }
                          }}
                        >
                          <TableCell sx={{ width: 52, px: 1.25 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {email.is_read ? (
                                <DraftsIcon sx={{ fontSize: 18, color: 'action.disabled' }} />
                              ) : (
                                <MailIcon sx={{ fontSize: 18, color: 'info.main' }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'inherit' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                                {isSentFolderPath(currentFolder) ? cleanRecipientName(email.to) : cleanSenderName(email.from)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isPecMessage(email.subject) && (
                                  <LockIcon fontSize="small" color="action" sx={{ fontSize: 16 }} />
                                )}
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {cleanSubject(email.subject)}
                                </Typography>
                                {!!email.has_attachments && (
                                  <Chip
                                    icon={<AttachmentIcon sx={{ fontSize: 14 }} />}
                                    label="Allegato"
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem', width: 'fit-content', '.MuiChip-label': { px: 1 } }}
                                  />
                                )}
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {bodyPreviewByUid[previewKey(email.uid, currentFolder)] || ' '}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 700, lineHeight: 1.2 }}>
                                {formatAccountLabel(account?.email) || '-'}
                              </Typography>
                              <Chip
                                icon={isPecAccount ? <LockIcon sx={{ fontSize: 14 }} /> : <EmailIcon sx={{ fontSize: 14 }} />}
                                label={isPecAccount ? 'PEC' : 'E-mail'}
                                size="small"
                                variant="outlined"
                                color={isPecAccount ? 'secondary' : 'primary'}
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  width: 'fit-content',
                                  '.MuiChip-label': { px: 1 }
                                }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ width: 260, overflow: 'visible', pr: 1.5 }}>
                            <Box sx={{ position: 'relative', height: 34 }}>
                              <Box className="workflow-default" sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                                {(() => {
                                  const stato = email?.stato_lavorazione || 'nuovo';
                                  const riepilogo = String(email?.riepilogo_lavorazione || '');
                                  const isNonGestire = stato === 'chiuso' && /non\s*gestire/i.test(riepilogo);
                                  const statoLabel =
                                    {
                                      nuovo: 'Nuovo',
                                      in_lavorazione: 'In lavorazione',
                                      in_attesa: 'In attesa',
                                      chiuso: isNonGestire ? 'Non gestire' : 'Risolto'
                                    }[stato] || 'Nuovo';
                                  const statoColor =
                                    {
                                      nuovo: 'default',
                                      in_lavorazione: 'info',
                                      in_attesa: 'warning',
                                      chiuso: isNonGestire ? 'error' : 'success'
                                    }[stato] || 'default';
                                  const statoIcon =
                                    {
                                      nuovo: <AssignIcon sx={{ fontSize: 16 }} />,
                                      in_lavorazione: <ClaimIcon sx={{ fontSize: 16 }} />,
                                      in_attesa: <ScheduleIcon sx={{ fontSize: 16 }} />,
                                      chiuso: isNonGestire ? <NonGestireIcon sx={{ fontSize: 16, color: 'error.main' }} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />
                                    }[stato] || <AssignIcon sx={{ fontSize: 16 }} />;

                                  const assignedId =
                                    email?.assegnato_a_user_id != null ? Number(email.assegnato_a_user_id) : null;
                                  const assignedName = String(
                                    email?.assegnato_a_nome || email?.assegnato_a_email || (assignedId ? `Utente #${assignedId}` : '')
                                  );
                                  const lastActor = String(
                                    email?.ultima_attivita_da_nome || email?.ultima_attivita_da_email || (email?.ultima_attivita_da_user_id ? `Utente #${String(email.ultima_attivita_da_user_id)}` : '')
                                  );

                                  const showStatus = Boolean(assignedId) || stato !== 'nuovo';
                                  const chipLabel = showStatus ? statoLabel : 'Non assegnato';
                                  const chipColor = showStatus ? statoColor : 'default';
                                  const chipIcon = showStatus ? statoIcon : <AssignIcon sx={{ fontSize: 16 }} />;

                                  return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                      <Chip
                                        size="small"
                                        variant="outlined"
                                        icon={chipIcon}
                                        label={chipLabel}
                                        color={chipColor}
                                        sx={{ height: 22, fontSize: '0.75rem', '.MuiChip-label': { px: 1 }, width: 'fit-content' }}
                                      />
                                      {((isNonGestire && lastActor) || assignedName || showStatus) && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          sx={{ fontWeight: 400, lineHeight: 1.1 }}
                                          noWrap
                                        >
                                          {isNonGestire ? (lastActor ? `Da: ${lastActor}` : 'Non gestire') : assignedName ? `Assegnato: ${assignedName}` : 'Non assegnato'}
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })()}
                              </Box>
                              <Box
                                className="workflow-actions"
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  pl: 0.25,
                                  pr: 0.75
                                }}
                              >
                                <Tooltip title="Prendi in carico">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleWorkflowAction('claim', email);
                                    }}
                                    sx={(theme) => ({
                                      width: 32,
                                      height: 32,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.10)' : 'rgba(34, 197, 94, 0.08)',
                                      color: 'success.main',
                                      '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(34, 197, 94, 0.16)' : 'rgba(34, 197, 94, 0.12)'
                                      }
                                    })}
                                  >
                                    <ClaimIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Assegna">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleWorkflowAction('assign', email);
                                    }}
                                    sx={(theme) => ({
                                      width: 32,
                                      height: 32,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.10)' : 'rgba(59, 130, 246, 0.08)',
                                      color: 'info.main',
                                      '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.16)' : 'rgba(59, 130, 246, 0.12)'
                                      }
                                    })}
                                  >
                                    <AssignIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Nota">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleWorkflowAction('note', email);
                                    }}
                                    sx={(theme) => ({
                                      width: 32,
                                      height: 32,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(168, 85, 247, 0.10)' : 'rgba(168, 85, 247, 0.08)',
                                      color: 'secondary.main',
                                      '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(168, 85, 247, 0.16)' : 'rgba(168, 85, 247, 0.12)'
                                      }
                                    })}
                                  >
                                    <NoteIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                                {String(email?.stato_lavorazione || 'nuovo') !== 'chiuso' && (
                                  <Tooltip title="Non gestire">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleWorkflowAction('non_gestire', email);
                                      }}
                                      sx={(theme) => ({
                                        width: 32,
                                        height: 32,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.35 : 0.24),
                                        bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                                        color: 'error.main',
                                        '&:hover': {
                                          bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.22 : 0.14)
                                        }
                                      })}
                                    >
                                      <NonGestireIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Cronologia">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleWorkflowAction('history', email);
                                    }}
                                    sx={(theme) => ({
                                      width: 32,
                                      height: 32,
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.10)' : 'rgba(148, 163, 184, 0.08)',
                                      color: 'text.secondary',
                                      '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.16)' : 'rgba(148, 163, 184, 0.12)'
                                      }
                                    })}
                                  >
                                    <HistoryIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', whiteSpace: 'nowrap', width: 110, fontWeight: 700 }}>
                            {formatListDate(email.date)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>
              <TablePagination
                sx={{ flexShrink: 0 }}
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalEmails}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('email.rowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${t('common.of')} ${count}`}
              />
            </Box>
          )}
        </Paper>
      </Box>

      {/* Receipt Details Dialog */}
      <Dialog 
          open={receiptDialog.open} 
          onClose={handleCloseReceipt}
          maxWidth="md"
          fullWidth
      >
          {receiptDialog.receipt && (
              <>
                  <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2, bgcolor: '#f8f9fa' }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {/* Subject */}
                          <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#333' }}>
                              {cleanSubject(receiptDialog.receipt.subject)}
                          </Typography>
                          
                          {/* Badge Status */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {renderReceiptIcon(getReceiptType(receiptDialog.receipt.subject))}
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: '#4caf50' }}>
                                  {getReceiptType(receiptDialog.receipt.subject) === 'accettazione' ? 'Ricevuta di Accettazione' :
                                   getReceiptType(receiptDialog.receipt.subject) === 'consegna' ? 'Ricevuta di Consegna' :
                                   'Ricevuta PEC'}
                              </Typography>
                              <Box sx={{ bgcolor: '#e0e0e0', borderRadius: 1, px: 1, py: 0.2, ml: 1 }}>
                                  <Typography variant="caption" sx={{ color: '#555', fontWeight: 500 }}>
                                      In arrivo
                                  </Typography>
                              </Box>
                          </Box>
                          
                          {/* Date */}
                          <Typography variant="caption" color="text.secondary">
                              {new Date(receiptDialog.receipt.date).toLocaleString()}
                          </Typography>
                      </Box>
                  </DialogTitle>
                  <DialogContent sx={{ mt: 2, minHeight: '400px', p: 3 }}>
                      {receiptDialog.loading ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                              <CircularProgress />
                          </Box>
                      ) : receiptDialog.body ? (
                          <Box>
                              {/* Attachments Cards */}
                              {receiptDialog.body.attachments && receiptDialog.body.attachments.length > 0 && (
                                  <Box sx={{ mb: 4 }}>
                                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                          {receiptDialog.body.attachments.map((att, idx) => (
                                              <Paper
                                                  key={idx}
                                                  variant="outlined"
                                                  sx={{ 
                                                      p: 1.5, 
                                                      display: 'flex', 
                                                      alignItems: 'center', 
                                                      gap: 2,
                                                      borderRadius: 2,
                                                      cursor: 'pointer',
                                                      minWidth: 200,
                                                      borderColor: '#e0e0e0',
                                                      '&:hover': { bgcolor: '#f5f5f5', borderColor: '#ccc' }
                                                  }}
                                                  onClick={() => handleDownloadReceiptAttachment(receiptDialog.receipt, idx, att.filename)}
                                              >
                                                  <Box sx={{ 
                                                      width: 40, height: 40, 
                                                      bgcolor: '#f5f5f5', 
                                                      borderRadius: '50%', 
                                                      display: 'flex', alignItems: 'center', justifyContent: 'center' 
                                                  }}>
                                                      {getAttachmentIcon(att.filename)}
                                                  </Box>
                                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#333' }}>
                                                          {att.filename}
                                                      </Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                          Clicca per scaricare
                                                      </Typography>
                                                  </Box>
                                              </Paper>
                                          ))}
                                      </Box>
                                      <Button 
                                          startIcon={<DownloadIcon />} 
                                          sx={{ mt: 1, textTransform: 'none', color: '#666' }}
                                          size="small"
                                      >
                                          Scarica tutto
                                      </Button>
                                  </Box>
                              )}
                              
                              {/* Content Body */}
                              <Box sx={{ mt: 2 }}>
                                  {renderReceiptBody(receiptDialog.body)}
                              </Box>
                          </Box>
                      ) : (
                          <Typography color="error">Impossibile caricare il contenuto della ricevuta.</Typography>
                      )}
                  </DialogContent>
                  <DialogActions sx={{ borderTop: '1px solid #eee', p: 2, bgcolor: '#f8f9fa' }}>
                      <Button onClick={handleCloseReceipt} variant="outlined" color="primary">Chiudi</Button>
                  </DialogActions>
              </>
          )}
      </Dialog>

      <Dialog
        open={claimDialog.open}
        onClose={closeClaimDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
              border: '1px solid',
              borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.35 : 0.25),
              color: 'primary.main'
            })}
          >
            <ClaimIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
              Presa in carico
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }} noWrap>
              Il messaggio verrà assegnato a te con timestamp automatico
            </Typography>
          </Box>
          <Tooltip title="Chiudi">
            <IconButton
              onClick={closeClaimDialog}
              size="small"
              sx={(theme) => ({
                ml: 'auto',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 1,
              borderColor: 'divider',
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(255, 255, 255, 0.06)'
                  : 'rgba(0, 0, 0, 0.04)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Box
                  sx={(theme) => ({
                    width: 38,
                    height: 38,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                    color: 'info.main',
                    flex: '0 0 auto'
                  })}
                >
                  <MailIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }} noWrap>
                    {claimDialog.email
                      ? (isSentFolderPath(currentFolder) ? cleanRecipientName(claimDialog.email.to) : cleanSenderName(claimDialog.email.from))
                      : ''}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 400, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {claimDialog.email ? cleanSubject(claimDialog.email.subject) : ''}
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                icon={claimDialog.email && !claimDialog.email.is_read ? <MailIcon sx={{ fontSize: 16 }} /> : <DraftsIcon sx={{ fontSize: 16 }} />}
                label={claimDialog.email && !claimDialog.email.is_read ? 'Nuovo' : 'Letto'}
                sx={(theme) => {
                  const isNew = Boolean(claimDialog.email && !claimDialog.email.is_read);
                  const base = isNew ? theme.palette.error.main : theme.palette.text.secondary;
                  return {
                    flex: '0 0 auto',
                    borderRadius: 1,
                    height: 26,
                    bgcolor: alpha(base, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                    color: base,
                    border: '1px solid',
                    borderColor: alpha(base, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                    fontWeight: 500,
                    '& .MuiChip-icon': { color: 'inherit', ml: 0.75 }
                  };
                }}
              />
            </Box>
          </Paper>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={(theme) => ({
                      width: 30,
                      height: 30,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                      color: 'info.main',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                      flex: '0 0 auto'
                    })}
                  >
                    <ScheduleIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, lineHeight: 1.1 }}>
                      {claimDialog.email?.date ? formatTimeHHMM(new Date(claimDialog.email.date)) : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                      {claimDialog.email?.date ? new Date(claimDialog.email.date).toLocaleDateString('it-IT') : 'Ricevuto'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={(theme) => ({
                      width: 30,
                      height: 30,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                      color: 'warning.main',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                      flex: '0 0 auto'
                    })}
                  >
                    <AssignIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, lineHeight: 1.1 }}>
                      —
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                      Nessun operatore
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box
                    sx={(theme) => ({
                      width: 30,
                      height: 30,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                      color: 'success.main',
                      border: '1px solid',
                      borderColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                      flex: '0 0 auto'
                    })}
                  >
                    <NoteIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 500, lineHeight: 1.1 }}>
                      0
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }}>
                      Interventi
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Operatore corrente (tu)
            </Typography>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                  <Box
                    sx={(theme) => ({
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
                      color: 'primary.main',
                      fontWeight: 700,
                      flex: '0 0 auto'
                    })}
                  >
                    {String(user?.name || user?.email || 'U').slice(0, 2).toUpperCase()}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }} noWrap>
                      {user?.name || 'Operatore'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {user?.email || ''}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={(theme) => ({
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                    color: 'success.main',
                    flex: '0 0 auto'
                  })}
                >
                  <CheckCircleIcon sx={{ fontSize: 18 }} />
                </Box>
              </Box>
            </Paper>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Priorità
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1 }}>
              {[
                { key: 'bassa', label: 'Bassa' },
                { key: 'media', label: 'Media' },
                { key: 'alta', label: 'Alta' },
                { key: 'urgente', label: 'Urgente' }
              ].map((p) => {
                const selected = claimPriority === p.key;
                return (
                  <Button
                    key={p.key}
                    variant={selected ? 'contained' : 'outlined'}
                    color={selected ? 'primary' : 'inherit'}
                    onClick={() => setClaimPriority(p.key)}
                    sx={{
                      borderRadius: 1,
                      textTransform: 'none',
                      fontWeight: 500
                    }}
                  >
                    {p.label}
                  </Button>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Nota iniziale (opzionale)
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={claimNote}
              onChange={(e) => setClaimNote(e.target.value)}
              placeholder="Es. Verificare contratto n. 4521 prima di rispondere…"
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={closeClaimDialog}
            color="inherit"
            sx={{ borderRadius: 1, fontWeight: 500 }}
            disabled={claimSubmitting}
          >
            Annulla
          </Button>
          <Button
            variant="contained"
            disabled={claimSubmitting}
            onClick={async () => {
              if (!claimDialog.email) return;
              setClaimSubmitting(true);
              try {
                await api.post(
                  `/email-accounts/${accountId}/emails/${claimDialog.email.uid}/claim`,
                  { priority: claimPriority, note: claimNote },
                  { params: { folder: currentFolder } }
                );
                toast.success('Presa in carico registrata');
                closeClaimDialog();
                fetchEmails();
              } catch (e) {
                toast.error(e?.response?.data?.message || 'Errore durante la presa in carico');
              } finally {
                setClaimSubmitting(false);
              }
            }}
            startIcon={<ClaimIcon />}
            color="primary"
            sx={{ borderRadius: 1, fontWeight: 600 }}
          >
            Prendi in carico
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={assignDialog.open}
        onClose={closeAssignDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
              border: '1px solid',
              borderColor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
              color: 'secondary.main'
            })}
          >
            <AssignIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
              Assegna messaggio
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }} noWrap>
              Scegli l'operatore e la priorità
            </Typography>
          </Box>
          <Tooltip title="Chiudi">
            <IconButton
              onClick={closeAssignDialog}
              size="small"
              sx={(theme) => ({
                ml: 'auto',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 1,
              borderColor: 'divider',
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)')
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Box
                  sx={(theme) => ({
                    width: 38,
                    height: 38,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                    color: 'info.main',
                    flex: '0 0 auto'
                  })}
                >
                  <MailIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }} noWrap>
                    {assignDialog.email
                      ? (isSentFolderPath(currentFolder) ? cleanRecipientName(assignDialog.email.to) : cleanSenderName(assignDialog.email.from))
                      : ''}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 400, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {assignDialog.email ? cleanSubject(assignDialog.email.subject) : ''}
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                icon={assignDialog.email && !assignDialog.email.is_read ? <MailIcon sx={{ fontSize: 16 }} /> : <DraftsIcon sx={{ fontSize: 16 }} />}
                label={assignDialog.email && !assignDialog.email.is_read ? 'Nuovo' : 'Letto'}
                sx={(theme) => {
                  const isNew = Boolean(assignDialog.email && !assignDialog.email.is_read);
                  const base = isNew ? theme.palette.error.main : theme.palette.text.secondary;
                  return {
                    flex: '0 0 auto',
                    borderRadius: 1,
                    height: 26,
                    bgcolor: alpha(base, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                    color: base,
                    border: '1px solid',
                    borderColor: alpha(base, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                    fontWeight: 500,
                    '& .MuiChip-icon': { color: 'inherit', ml: 0.75 }
                  };
                }}
              />
            </Box>
          </Paper>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Seleziona operatore
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Cerca per nome o email…"
              value={assignOperatorsSearch}
              onChange={(e) => setAssignOperatorsSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  loadAssignOperators(assignOperatorsSearch);
                }
              }}
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'grid', gap: 1 }}>
              {(assignOperatorsLoading ? [] : assignOperators).map((op) => {
                const selected = Number(assignOperatoreId) === Number(op.id);
                const carico = op?.messaggi_attivi != null ? Number(op.messaggi_attivi) : 0;
                const isAvailable = carico < 6;
                const nome = op?.name || op?.nome || op?.email || '';
                const email = op?.email || '';
                const initials = String(nome || '')
                  .split(' ')
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((p) => p[0])
                  .join('')
                  .toUpperCase();
                return (
                  <Paper
                    key={op.id}
                    variant="outlined"
                    onClick={() => setAssignOperatoreId(Number(op.id))}
                    sx={(theme) => ({
                      p: 1.25,
                      borderRadius: 1,
                      borderColor: selected ? alpha(theme.palette.primary.main, 0.45) : 'divider',
                      bgcolor: selected ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10) : 'transparent',
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.06) }
                    })}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        <Box
                          sx={(theme) => ({
                            width: 34,
                            height: 34,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                            color: 'secondary.main',
                            fontWeight: 700,
                            flex: '0 0 auto'
                          })}
                        >
                          {initials || 'U'}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                            {nome}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }} noWrap>
                            {email} · {carico} messaggi attivi
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          size="small"
                          label={isAvailable ? 'disponibile' : 'occupata'}
                          sx={(theme) => ({
                            height: 22,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            bgcolor: alpha(isAvailable ? theme.palette.success.main : theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                            color: isAvailable ? 'success.main' : 'warning.main',
                            border: '1px solid',
                            borderColor: alpha(isAvailable ? theme.palette.success.main : theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                            '.MuiChip-label': { px: 1 }
                          })}
                        />
                        <Radio checked={selected} value={op.id} onChange={() => setAssignOperatoreId(Number(op.id))} />
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
              {assignOperatorsLoading ? (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                    Caricamento operatori…
                  </Typography>
                </Paper>
              ) : null}
              {!assignOperatorsLoading && assignOperators.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                    Nessun operatore trovato
                  </Typography>
                </Paper>
              ) : null}
            </Box>
          </Box>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                Priorità
              </Typography>
              <FormControl fullWidth>
                <Select value={assignPriority} onChange={(e) => setAssignPriority(e.target.value)} displayEmpty>
                  <MenuItem value="bassa">Bassa</MenuItem>
                  <MenuItem value="media">Media</MenuItem>
                  <MenuItem value="alta">Alta</MenuItem>
                  <MenuItem value="urgente">Urgente</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                Scadenza (opzionale)
              </Typography>
              <TextField
                fullWidth
                type="date"
                value={assignDueDate}
                onChange={(e) => setAssignDueDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={closeAssignDialog} color="inherit" sx={{ borderRadius: 1, fontWeight: 500 }} disabled={assignSubmitting}>
            Annulla
          </Button>
          <Button
            variant="contained"
            startIcon={<AssignIcon />}
            disabled={!assignOperatoreId || assignSubmitting}
            onClick={async () => {
              if (!assignDialog.email || !assignOperatoreId) return;
              const selectedOp = assignOperators.find((o) => Number(o.id) === Number(assignOperatoreId));
              const label = selectedOp?.name || selectedOp?.email || 'Operatore';
              setAssignSubmitting(true);
              try {
                await api.post(
                  `/email-accounts/${accountId}/emails/${assignDialog.email.uid}/assign`,
                  { user_id: Number(assignOperatoreId), priority: assignPriority, due_date: assignDueDate || null },
                  { params: { folder: currentFolder } }
                );
                toast.success(`Assegnato a ${label}`);
                closeAssignDialog();
                fetchEmails();
              } catch (e) {
                toast.error(e?.response?.data?.message || 'Errore durante l’assegnazione');
              } finally {
                setAssignSubmitting(false);
              }
            }}
            sx={{ borderRadius: 1, fontWeight: 600, textTransform: 'none' }}
          >
            {`Assegna${assignOperatoreId ? ` a ${(assignOperators.find((o) => Number(o.id) === Number(assignOperatoreId))?.name || 'Operatore')}` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={noteDialog.open}
        onClose={closeNoteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
              border: '1px solid',
              borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
              color: 'warning.main'
            })}
          >
            <NoteIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
              Nota interna
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }} noWrap>
              Visibile solo agli operatori, salvata in cronologia
            </Typography>
          </Box>
          <Tooltip title="Chiudi">
            <IconButton
              onClick={closeNoteDialog}
              size="small"
              sx={(theme) => ({
                ml: 'auto',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 1,
              borderColor: 'divider',
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)')
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Box
                  sx={(theme) => ({
                    width: 38,
                    height: 38,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                    color: 'info.main',
                    flex: '0 0 auto'
                  })}
                >
                  <MailIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }} noWrap>
                    {noteDialog.email
                      ? (isSentFolderPath(currentFolder) ? cleanRecipientName(noteDialog.email.to) : cleanSenderName(noteDialog.email.from))
                      : ''}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 400, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {noteDialog.email ? cleanSubject(noteDialog.email.subject) : ''}
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                label="In attesa"
                sx={(theme) => ({
                  flex: '0 0 auto',
                  borderRadius: 1,
                  height: 26,
                  bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
                  color: 'warning.main',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                  fontWeight: 500
                })}
              />
            </Box>
          </Paper>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
              Testo nota
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Es. In attesa di conferma dal responsabile prima di rispondere. Verificare il contratto n. 4521…"
            />
          </Box>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                Tipo nota
              </Typography>
              <FormControl fullWidth>
                <Select value={noteType} onChange={(e) => setNoteType(e.target.value)} displayEmpty>
                  <MenuItem value="operativa">Nota operativa</MenuItem>
                  <MenuItem value="promemoria">Promemoria</MenuItem>
                  <MenuItem value="richiesta">Richiesta</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                Cambia stato
              </Typography>
              <FormControl fullWidth>
                <Select value={noteStatusChange} onChange={(e) => setNoteStatusChange(e.target.value)} displayEmpty>
                  <MenuItem value="nessuna">Nessuna modifica</MenuItem>
                  <MenuItem value="nuovo">Nuovo</MenuItem>
                  <MenuItem value="in_lavorazione">In lavorazione</MenuItem>
                  <MenuItem value="in_attesa">In attesa</MenuItem>
                  <MenuItem value="chiuso">Risolto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Paper
            variant="outlined"
            sx={(theme) => ({
              mt: 2,
              p: 1.5,
              borderRadius: 1,
              borderColor: 'divider',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
              display: 'flex',
              alignItems: 'center',
              gap: 1.25
            })}
          >
            <Box
              sx={(theme) => ({
                width: 30,
                height: 30,
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.10 : 0.06),
                color: 'text.secondary',
                flex: '0 0 auto'
              })}
            >
              <LockIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
              La nota sarà visibile solo agli operatori del team. Non viene inviata al mittente del messaggio.
            </Typography>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={closeNoteDialog} color="inherit" sx={{ borderRadius: 1, fontWeight: 500 }} disabled={noteSubmitting}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<NoteIcon />}
            disabled={!noteText.trim() || noteSubmitting}
            onClick={async () => {
              if (!noteDialog.email) return;
              setNoteSubmitting(true);
              try {
                await api.post(
                  `/email-accounts/${accountId}/emails/${noteDialog.email.uid}/notes`,
                  { testo: noteText, tipo: noteType, cambia_stato: noteStatusChange },
                  { params: { folder: currentFolder } }
                );
                toast.success('Nota salvata');
                closeNoteDialog();
                fetchEmails();
                if (historyDialog.open && historyDialog.email?.uid === noteDialog.email.uid) {
                  setHistoryLoading(true);
                  api
                    .get(`/email-accounts/${accountId}/emails/${noteDialog.email.uid}/history`, { params: { folder: currentFolder } })
                    .then((res) => setHistoryData(res.data || null))
                    .finally(() => setHistoryLoading(false));
                }
              } catch (e) {
                toast.error(e?.response?.data?.message || 'Errore durante il salvataggio della nota');
              } finally {
                setNoteSubmitting(false);
              }
            }}
            sx={{ borderRadius: 1, fontWeight: 600 }}
          >
            Salva nota
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={nonGestireDialog.open}
        onClose={closeNonGestireDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
              border: '1px solid',
              borderColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.35 : 0.24),
              color: 'error.main'
            })}
          >
            <NonGestireIcon sx={{ fontSize: 20, color: 'error.main' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
              Non gestire
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }} noWrap>
              Traccia l’operatore e il motivo, e rimuove il messaggio dalle viste operative
            </Typography>
          </Box>
          <Tooltip title="Chiudi">
            <IconButton
              onClick={closeNonGestireDialog}
              size="small"
              sx={(theme) => ({
                ml: 'auto',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <DialogContent sx={{ pt: 2.5 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 1,
              borderColor: 'divider',
              bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)')
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                <Box
                  sx={(theme) => ({
                    width: 38,
                    height: 38,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                    border: '1px solid',
                    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                    color: 'info.main',
                    flex: '0 0 auto'
                  })}
                >
                  <MailIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 500 }} noWrap>
                    {nonGestireDialog.email
                      ? (isSentFolderPath(currentFolder) ? cleanRecipientName(nonGestireDialog.email.to) : cleanSenderName(nonGestireDialog.email.from))
                      : ''}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 400, display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {nonGestireDialog.email ? cleanSubject(nonGestireDialog.email.subject) : ''}
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                icon={<NonGestireIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                label="Non gestire"
                sx={(theme) => ({
                  flex: '0 0 auto',
                  borderRadius: 1,
                  height: 26,
                  bgcolor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
                  color: 'error.main',
                  border: '1px solid',
                  borderColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.35 : 0.24),
                  fontWeight: 500
                })}
              />
            </Box>
          </Paper>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                Motivo
              </Typography>
              <FormControl fullWidth>
                <Select value={nonGestireMotivo} onChange={(e) => setNonGestireMotivo(e.target.value)} displayEmpty>
                  <MenuItem value="newsletter">Newsletter</MenuItem>
                  <MenuItem value="notifica_automatica">Notifica automatica</MenuItem>
                  <MenuItem value="spam">Spam</MenuItem>
                  <MenuItem value="duplicato">Duplicato</MenuItem>
                  <MenuItem value="informativa">Informativa</MenuItem>
                  <MenuItem value="altro">Altro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                Dettagli (opzionale)
              </Typography>
              <TextField
                fullWidth
                value={nonGestireDettagli}
                onChange={(e) => setNonGestireDettagli(e.target.value)}
                placeholder="Es. YouTube / notifiche di sistema…"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={closeNonGestireDialog} color="inherit" sx={{ borderRadius: 1, fontWeight: 500 }} disabled={nonGestireSubmitting}>
            Annulla
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<NonGestireIcon sx={{ color: 'inherit' }} />}
            disabled={!String(nonGestireMotivo || '').trim() || nonGestireSubmitting}
            onClick={async () => {
              if (!nonGestireDialog.email) return;
              setNonGestireSubmitting(true);
              try {
                await api.post(
                  `/email-accounts/${accountId}/emails/${nonGestireDialog.email.uid}/non-gestire`,
                  { motivo: nonGestireMotivo, dettagli: nonGestireDettagli },
                  { params: { folder: currentFolder } }
                );
                toast.success('Classificata come non gestire');
                closeNonGestireDialog();
                fetchEmails();
                if (historyDialog.open && historyDialog.email?.uid === nonGestireDialog.email.uid) {
                  setHistoryLoading(true);
                  api
                    .get(`/email-accounts/${accountId}/emails/${nonGestireDialog.email.uid}/history`, { params: { folder: currentFolder } })
                    .then((res) => setHistoryData(res.data || null))
                    .finally(() => setHistoryLoading(false));
                }
              } catch (e) {
                toast.error(e?.response?.data?.message || 'Errore durante la classificazione');
              } finally {
                setNonGestireSubmitting(false);
              }
            }}
            sx={{ borderRadius: 1, fontWeight: 600 }}
          >
            Conferma
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={historyDialog.open}
        onClose={closeHistoryDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 1 } }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1.25, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={(theme) => ({
              width: 38,
              height: 38,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.18 : 0.10),
              border: '1px solid',
              borderColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
              color: 'success.main'
            })}
          >
            <HistoryIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="h6" sx={{ lineHeight: 1.2, fontWeight: 600 }}>
              Cronologia messaggio
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }} noWrap>
              {historyDialog.email
                ? `${cleanSenderName(historyDialog.email.from || '')} · Re: ${cleanSubject(historyDialog.email.subject || '')}`
                : ''}
            </Typography>
          </Box>
          <Tooltip title="Chiudi">
            <IconButton
              onClick={closeHistoryDialog}
              size="small"
              sx={(theme) => ({
                ml: 'auto',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
              })}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </DialogTitle>

        <Tabs value={historyTab} onChange={(e, v) => setHistoryTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label={`Cronologia (${historyData?.events ? historyData.events.length : 0})`} />
          <Tab label={`Note (${historyData?.notes ? historyData.notes.length : 0})`} />
          <Tab label={`Allegati (${historyAttachments ? historyAttachments.length : 0})`} />
          <Tab label="Riepilogo" />
        </Tabs>

        <DialogContent sx={{ pt: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8} lg={9}>
              {historyTab === 0 && (
                <Box sx={{ pr: { md: 1 } }}>
                  {historyLoading ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Caricamento cronologia…
                      </Typography>
                    </Paper>
                  ) : (() => {
                    const timeline =
                      historyData?.events && historyData.events.length
                        ? mapBackendTimeline(historyData.events)
                        : buildBaseTimeline(historyDialog.email);
                    return timeline.map((ev, idx) => (
                      <Box key={`${ev.type}-${idx}`} sx={{ display: 'flex', gap: 1.5, pb: 2 }}>
                        <Box sx={{ width: 22, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <Box
                            sx={(theme) => ({
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: getTimelineDotColor(ev.type),
                              boxShadow: `0 0 0 4px ${alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.9 : 1)}`
                            })}
                          />
                          {idx !== timeline.length - 1 && <Box sx={{ width: 2, flex: 1, bgcolor: 'divider', mt: 0.75 }} />}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Box
                                sx={(theme) => ({
                                  width: 28,
                                  height: 28,
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.10 : 0.06),
                                  color: 'text.secondary',
                                  flex: '0 0 auto'
                                })}
                              >
                                {getTimelineIcon(ev.type)}
                              </Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                                {ev.titolo}
                              </Typography>
                              <Chip
                                size="small"
                                label={ev.tag}
                                color={ev.tagColor}
                                variant="outlined"
                                sx={{ height: 22, fontSize: '0.75rem', '.MuiChip-label': { px: 1 } }}
                              />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap' }}>
                              {formatHistoryWhen(ev.when)}
                            </Typography>
                          </Box>
                          {ev.highlight ? (
                            <Paper
                              variant="outlined"
                              sx={(theme) => ({
                                mt: 1,
                                p: 1.25,
                                borderRadius: 1,
                                borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                                bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.12 : 0.06)
                              })}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                {ev.descrizione}
                              </Typography>
                            </Paper>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
                              {ev.descrizione}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ));
                  })()}
                </Box>
              )}

              {historyTab === 1 && (
                <Box sx={{ pr: { md: 1 } }}>
                  {historyLoading ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Caricamento note…
                      </Typography>
                    </Paper>
                  ) : null}
                  {!historyLoading && Array.isArray(historyData?.notes) && historyData.notes.length
                    ? historyData.notes.map((n) => (
                        <Paper key={n.id} variant="outlined" sx={{ p: 1.5, borderRadius: 1, borderColor: 'divider', mb: 1.25 }}>
                          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                            <Box
                              sx={(theme) => ({
                                width: 34,
                                height: 34,
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.14 : 0.10),
                                border: '1px solid',
                                borderColor: alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                                color: 'warning.main',
                                flex: '0 0 auto'
                              })}
                            >
                              <NoteIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                                  Nota interna
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap' }}>
                                  {formatHistoryWhen(n.creata_il)}
                                </Typography>
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 400, mt: 0.5 }}>
                                {n.testo}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, mt: 0.75, display: 'block' }}>
                                {n.autore_nome || n.autore_email || 'Operatore'}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      ))
                    : !historyLoading && (
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                            Nessuna nota presente
                          </Typography>
                        </Paper>
                      )}
                </Box>
              )}

              {historyTab === 2 && (
                <Box sx={{ pr: { md: 1 } }}>
                  {historyAttachmentsLoading ? (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Caricamento allegati…
                      </Typography>
                    </Paper>
                  ) : Array.isArray(historyAttachments) && historyAttachments.length ? (
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch', minHeight: 420 }}>
                      {(() => {
                        const total = historyAttachments.reduce((acc, a) => acc + (a?.size != null ? Number(a.size) : 0), 0);
                        const totalLabel = formatFileSize(total);
                        return (
                          <Paper variant="outlined" sx={{ width: 320, borderRadius: 1, borderColor: 'divider', overflow: 'hidden', flexShrink: 0 }}>
                            <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {`${historyAttachments.length} allegati${totalLabel ? ` · ${totalLabel} totali` : ''}`}
                              </Typography>
                              <Tooltip title="Scarica tutti">
                                <IconButton
                                  size="small"
                                  onClick={handleDownloadAllHistoryAttachments}
                                  sx={(theme) => ({
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.6 : 0.35),
                                    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
                                    color: 'info.main',
                                    '&:hover': { bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.22 : 0.12) }
                                  })}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                            <Box sx={{ p: 1.5, display: 'grid', gap: 1 }}>
                              {historyAttachments.map((att) => {
                                const active = historySelectedAttachment && String(historySelectedAttachment.index) === String(att.index);
                                const isPdf = String(att.content_type || '').toLowerCase().includes('pdf') || String(att.filename || '').toLowerCase().endsWith('.pdf');
                                const mimeLabel = String(att.content_type || 'application/octet-stream');
                                const previewOk = isHistoryAttachmentPreviewSupported(att);
                                return (
                                  <Paper
                                    key={String(att.id || att.index)}
                                    variant="outlined"
                                    onClick={() => handleSelectHistoryAttachment(att)}
                                    sx={(theme) => ({
                                      p: 1.25,
                                      borderRadius: 1,
                                      borderColor: active ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.7 : 0.45) : 'divider',
                                      bgcolor: active ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.08) : 'transparent',
                                      cursor: 'pointer'
                                    })}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                        <Box
                                          sx={(theme) => ({
                                            width: 34,
                                            height: 34,
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            bgcolor: alpha(isPdf ? theme.palette.error.main : theme.palette.info.main, theme.palette.mode === 'dark' ? 0.14 : 0.10),
                                            border: '1px solid',
                                            borderColor: alpha(isPdf ? theme.palette.error.main : theme.palette.info.main, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                                            color: isPdf ? 'error.main' : 'info.main',
                                            flex: '0 0 auto'
                                          })}
                                        >
                                          <DescriptionIcon sx={{ fontSize: 18 }} />
                                        </Box>
                                        <Box sx={{ minWidth: 0 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                                            {att.filename || 'allegato'}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400 }} noWrap>
                                            {(att.size != null ? `${formatFileSize(att.size)} • ` : '') + mimeLabel}
                                          </Typography>
                                          {!previewOk && (
                                            <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary', display: 'block' }} noWrap>
                                              Anteprima non disponibile
                                            </Typography>
                                          )}
                                        </Box>
                                      </Box>
                                      <Tooltip title="Scarica">
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleDownloadHistoryAttachment(att.index, att.filename);
                                          }}
                                          sx={{ borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                                        >
                                          <DownloadIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Paper>
                                );
                              })}
                            </Box>
                          </Paper>
                        );
                      })()}

                      <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, borderRadius: 1, borderColor: 'divider', overflow: 'hidden' }}>
                        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                            {historySelectedAttachment?.filename || 'Anteprima'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Scarica">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!historySelectedAttachment}
                                  onClick={() =>
                                    historySelectedAttachment
                                      ? handleDownloadHistoryAttachment(historySelectedAttachment.index, historySelectedAttachment.filename)
                                      : null
                                  }
                                  sx={(theme) => ({
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.6 : 0.35),
                                    bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.14 : 0.08),
                                    color: 'info.main',
                                    '&:hover': { bgcolor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.22 : 0.12) }
                                  })}
                                >
                                  <DownloadIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Apri in nuova scheda">
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={!historySelectedAttachment}
                                  onClick={() => {
                                    if (!historySelectedAttachment) return;
                                    openHistoryAttachmentInNewTab(historySelectedAttachment);
                                  }}
                                  sx={(theme) => ({
                                    width: 34,
                                    height: 34,
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.35 : 0.22),
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                                    color: 'text.secondary',
                                    '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)' }
                                  })}
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </Box>

                        <Box sx={{ height: 520, bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)') }}>
                          {historyPreviewLoading ? (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25 }}>
                              <CircularProgress size={20} />
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                                Caricamento anteprima…
                              </Typography>
                            </Box>
                          ) : historyPreviewError ? (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                                {historyPreviewError}
                              </Typography>
                            </Box>
                          ) : historyPreviewUrl ? (
                            (() => {
                              const t = String(historyPreviewType || '').toLowerCase();
                              const isImg = t.startsWith('image/');
                              if (isImg) {
                                return (
                                  <Box
                                    component="img"
                                    src={historyPreviewUrl}
                                    alt={historySelectedAttachment?.filename || 'allegato'}
                                    sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                                  />
                                );
                              }
                              return (
                                <iframe
                                  title={historySelectedAttachment?.filename || 'allegato'}
                                  src={historyPreviewUrl}
                                  style={{ width: '100%', height: '100%', border: 0 }}
                                />
                              );
                            })()
                          ) : (
                            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                                Seleziona un allegato per visualizzare l’anteprima
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Paper>
                    </Box>
                  ) : (
                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Nessun allegato presente
                      </Typography>
                    </Paper>
                  )}
                </Box>
              )}

              {historyTab === 3 && (
                <Box sx={{ pr: { md: 1 } }}>
                  {(() => {
                    const summary = getHistoryComputedSummary();
                    const operatoreLabel = getHistoryOperatorLabel();
                    const stato = historyData?.email?.stato_lavorazione || 'nuovo';
                    const statoLabel =
                      {
                        nuovo: 'Nuovo',
                        in_lavorazione: 'In lavorazione',
                        in_attesa: 'In attesa',
                        chiuso: 'Risolto'
                      }[stato] || 'Nuovo';
                    const statoColor =
                      {
                        nuovo: 'default',
                        in_lavorazione: 'info',
                        in_attesa: 'warning',
                        chiuso: 'success'
                      }[stato] || 'default';

                    return (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
                      Riepilogo lavorazione
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 1, columnGap: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Ricevuto
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {formatHistoryWhen(historyDialog.email?.date)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Primo intervento
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {summary.firstInterventionDelay}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Chiuso
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {summary.closedDelay}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Operatore
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: 'primary.main' }}>
                        {operatoreLabel}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                        Stato attuale
                      </Typography>
                      <Chip
                        size="small"
                        label={statoLabel}
                        color={statoColor}
                        variant="outlined"
                        sx={{ height: 24, fontSize: '0.75rem', '.MuiChip-label': { px: 1 }, justifySelf: 'end' }}
                      />
                    </Box>
                  </Paper>
                    );
                  })()}
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={4} lg={3}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
                  Riepilogo lavorazione
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 1, columnGap: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                    Ricevuto
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatHistoryWhen(historyDialog.email?.date)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                    Operatore
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: 'primary.main' }}>
                    {getHistoryOperatorLabel()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                    Stato attuale
                  </Typography>
                  <Chip
                    size="small"
                    label={
                      {
                        nuovo: 'Nuovo',
                        in_lavorazione: 'In lavorazione',
                        in_attesa: 'In attesa',
                        chiuso: 'Risolto'
                      }[historyData?.email?.stato_lavorazione || 'nuovo'] || 'Nuovo'
                    }
                    color={
                      {
                        nuovo: 'default',
                        in_lavorazione: 'info',
                        in_attesa: 'warning',
                        chiuso: 'success'
                      }[historyData?.email?.stato_lavorazione || 'nuovo'] || 'default'
                    }
                    variant="outlined"
                    sx={{ height: 24, fontSize: '0.75rem', '.MuiChip-label': { px: 1 }, justifySelf: 'end' }}
                  />
                </Box>
              </Paper>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.25 }}>
                  Azioni rapide
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<AssignIcon />}
                    onClick={() => {
                      if (historyDialog.email) openAssignDialog(historyDialog.email);
                    }}
                    sx={{ borderRadius: 1, textTransform: 'none', justifyContent: 'center', fontWeight: 600 }}
                  >
                    Riassegna
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<NoteIcon />}
                    onClick={() => {
                      if (historyDialog.email) openNoteDialog(historyDialog.email);
                    }}
                    sx={{ borderRadius: 1, textTransform: 'none', justifyContent: 'center', fontWeight: 600 }}
                  >
                    Aggiungi nota
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CheckCircleIcon />}
                    onClick={async () => {
                      if (!historyDialog.email) return;
                      try {
                        await api.post(
                          `/email-accounts/${accountId}/emails/${historyDialog.email.uid}/status`,
                          { stato: 'chiuso' },
                          { params: { folder: currentFolder } }
                        );
                        toast.success('Segnato come risolto');
                        setHistoryLoading(true);
                        api
                          .get(`/email-accounts/${accountId}/emails/${historyDialog.email.uid}/history`, { params: { folder: currentFolder } })
                          .then((res) => setHistoryData(res.data || null))
                          .finally(() => setHistoryLoading(false));
                        fetchEmails();
                      } catch (e) {
                        toast.error(e?.response?.data?.message || 'Errore durante l’aggiornamento dello stato');
                      }
                    }}
                    sx={(theme) => ({
                      borderRadius: 1,
                      textTransform: 'none',
                      justifyContent: 'center',
                      fontWeight: 600,
                      borderColor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.6 : 0.35),
                      bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
                      color: 'success.main',
                      '&:hover': { bgcolor: alpha(theme.palette.success.main, theme.palette.mode === 'dark' ? 0.24 : 0.14) }
                    })}
                  >
                    Segna come risolto
                  </Button>
                </Box>
              </Box>

              <Paper
                variant="outlined"
                sx={(theme) => ({
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  borderColor: 'divider',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'
                })}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                  Ultima nota
                </Typography>
                {historyData?.notes && historyData.notes.length ? (
                  <>
                    <Typography variant="body2" sx={{ fontWeight: 400 }}>
                      {historyData.notes[0].testo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, display: 'block', mt: 0.75 }}>
                      {(historyData.notes[0].autore_nome || historyData.notes[0].autore_email || 'Operatore') +
                        ' · ' +
                        formatHistoryWhen(historyData.notes[0].creata_il)}
                    </Typography>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                    Nessuna nota presente
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Compose Email Dialog */}
      <Dialog 
          open={composeOpen} 
          onClose={handleComposeClose}
          maxWidth="md"
          fullWidth
          disableEscapeKeyDown={sendingEmail}
      >
          <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
             <EmailIcon color="primary" /> {isPecAccount ? 'Invia PEC' : 'Invia Email'}
             {sendingEmail && <Chip label="Invio in corso..." color="warning" size="small" icon={<CircularProgress size={16} />} sx={{ ml: 2 }} />}
             {composeOriginalAttachmentsLoading && <Chip label="Caricamento allegati..." color="info" size="small" icon={<CircularProgress size={16} />} sx={{ ml: 2 }} />}
          </DialogTitle>
          
          <Tabs value={composeTab} onChange={(e, v) => setComposeTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
             <Tab label="Messaggio" icon={<EmailIcon />} iconPosition="start" />
             <Tab label="Opzioni" icon={<SettingsIcon />} iconPosition="start" />
          </Tabs>

          <DialogContent sx={{ mt: 2, minHeight: 400 }}>
             {composeTab === 0 && (
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: '0.9rem' }}>
                         <Typography variant="body2" fontWeight="bold">Mittente:</Typography>
                         <Typography variant="body2">{account?.name || account?.email} &lt;{account?.email}&gt;</Typography>
                     </Box>
                     
                     <TextField 
                         label="Destinatari *" 
                         fullWidth 
                         placeholder="email1@example.com, email2@example.com"
                         value={composeData.to}
                         onChange={(e) => handleComposeChange('to', e.target.value)}
                         size="small"
                         helperText="Inserisci gli indirizzi email separati da virgola"
                     />
                     
                     <Box sx={{ display: 'flex', gap: 2 }}>
                         <TextField 
                             label="CC (Copia Conoscenza)" 
                             fullWidth 
                             value={composeData.cc}
                             onChange={(e) => handleComposeChange('cc', e.target.value)}
                             size="small"
                         />
                         <TextField 
                             label="CCN (Copia Nascosta)" 
                             fullWidth 
                             value={composeData.bcc}
                             onChange={(e) => handleComposeChange('bcc', e.target.value)}
                             size="small"
                         />
                     </Box>

                     <TextField 
                         label="Oggetto *" 
                         fullWidth 
                         value={composeData.subject}
                         onChange={(e) => handleComposeChange('subject', e.target.value)}
                         size="small"
                     />
                     
                     <TextField 
                         label="Messaggio *" 
                         fullWidth 
                         multiline 
                         rows={12}
                         value={composeData.body}
                         onChange={(e) => handleComposeChange('body', e.target.value)}
                         placeholder="Scrivi qui il tuo messaggio..."
                     />
                 </Box>
             )}

             {composeTab === 1 && (
                 <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                     <FormControl fullWidth size="small">
                         <InputLabel>Priorità</InputLabel>
                         <Select
                             value={composeData.priority}
                             label="Priorità"
                             onChange={(e) => handleComposeChange('priority', e.target.value)}
                         >
                             <MenuItem value="low">Bassa</MenuItem>
                             <MenuItem value="normal">Normale</MenuItem>
                             <MenuItem value="high">Alta</MenuItem>
                         </Select>
                     </FormControl>

                     {!isPecAccount && (
                       <Box sx={{ display: 'flex', gap: 4 }}>
                           <FormControlLabel
                               control={<Switch checked={composeData.deliveryReceipt} onChange={(e) => handleComposeChange('deliveryReceipt', e.target.checked)} />}
                               label={
                                   <Box>
                                       <Typography variant="body2" fontWeight="bold">Ricevuta di Consegna</Typography>
                                       <Typography variant="caption" color="text.secondary">Richiedi conferma di consegna</Typography>
                                   </Box>
                               }
                           />
                           <FormControlLabel
                               control={<Switch checked={composeData.readReceipt} onChange={(e) => handleComposeChange('readReceipt', e.target.checked)} />}
                               label={
                                   <Box>
                                       <Typography variant="body2" fontWeight="bold">Ricevuta di Lettura</Typography>
                                       <Typography variant="caption" color="text.secondary">Richiedi conferma di lettura</Typography>
                                   </Box>
                               }
                           />
                       </Box>
                     )}
                     
                     {isPecAccount && (
                        <Box sx={{ mt: -2, mb: 2 }}>
                            <Typography variant="caption" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ErrorIcon fontSize="small" /> 
                                Nota: Per gli account PEC, le ricevute di accettazione e consegna sono sempre generate automaticamente dal sistema.
                            </Typography>
                        </Box>
                     )}
                     
                     <Box sx={{ border: '1px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: '#fafafa' }}>
                         <Button component="label" startIcon={<AttachmentIcon />}>
                             Allega file
                             <input type="file" hidden multiple onChange={handleFileChange} />
                         </Button>
                         <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                             Trascina qui i file o clicca per sfogliare
                         </Typography>
                     </Box>
                     
                     {composeData.attachments.length > 0 && (
                         <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                             {composeData.attachments.map((file, idx) => (
                                 <Chip 
                                     key={idx} 
                                     label={file.name} 
                                     onDelete={() => removeAttachment(idx)}
                                     variant="outlined"
                                 />
                             ))}
                         </Box>
                     )}
                 </Box>
             )}
          </DialogContent>

          <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
             <Button onClick={handleComposeClose} disabled={sendingEmail} color="inherit">Annulla</Button>
             <Button 
                 onClick={handleSendEmail} 
                 variant="contained" 
                 color="warning"
                 disabled={sendingEmail}
                 startIcon={sendingEmail ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                 sx={{ bgcolor: '#f57c00', '&:hover': { bgcolor: '#ef6c00' } }}
             >
                 {isPecAccount ? 'Invia PEC' : 'Invia Email'}
             </Button>
          </DialogActions>
      </Dialog>
    </Box>
  );
}
